import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, OverlayTrigger, Table, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import { Pie } from 'react-chartjs-2'; // Import the Pie chart component
import 'react-datepicker/dist/react-datepicker.css';
import { FaAngleDown, FaAngleLeft, FaAngleRight, FaAngleUp, FaTrash } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // import styles
import { Link, useParams, useNavigate } from 'react-router-dom';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL } from '../config';
import generateQuotationPDF from './GenerateQuote';
import LoadingSpinner, { LoadingButton } from './LoadingSpinner';
import { useTenant } from '../context/TenantContext';
import { usePermissions } from '../context/PermissionsContext';
import { getApiErrorMessage, readApiErrorMessage } from '../utils/apiErrorMessage';
import { PERMISSIONS } from '../constants/permissions';
import {
    ADMIN_ONLY_BULK_REMOVE_DEFAULTS_TIP,
    ADMIN_ONLY_DEFAULT_REMOVE_TIP,
    ADMIN_ONLY_MULTIPLIER_TIP,
} from '../utils/adminLineItemLocks';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { formatCarePlanId, formatQuotationId, formatCreatedDate } from '../utils/displayIds';
import SearchableCheckboxPicker from './SearchableCheckboxPicker';
import StaffTypeAbbr from './StaffTypeAbbr';
import { useStaffTypes } from '../hooks/useStaffTypes';
import LabourDifferenceSummary from './LabourDifferenceSummary';
import './PlanQuoteDetail.css';
import './ZeroPriceWarning.css';
import {
    getZeroMultiplierMessage,
    getZeroPriceMessage,
    hasLaborMinutes,
    isZeroPrice,
    summarizeZeroLineWarnings,
} from '../utils/zeroPriceWarnings';

ChartJS.register(ArcElement, Tooltip, Legend); // Register necessary components for Chart.js

function QuotationDetail({
    embedded = false,
    quotationId: quotationIdProp,
    customerId: customerIdProp,
    dateCreated: dateCreatedProp,
    onEditStateChange,
    onOpenPlan,
    // When embedded in ServicePlanQuoteShell the panel stays mounted while hidden.
    // Refetch care-plan hour totals whenever the quote tab is shown again / plan is saved.
    isActive = true,
    planRevision = 0,
    onHourTotalsChange,
    planHourTotals = null,
} = {}) {
    const navigate = useNavigate();
    const currentDate = new Date();
    const { currentTenant } = useTenant();
    const { getAbbreviation, getDisplayName } = useStaffTypes();
    const { hasPermission, hasRole } = usePermissions();
    const isAdmin = hasRole('Admin');

    // Add custom styles for the dropdown
    const dropdownStyles = {
        optgroup: {
            fontWeight: 'bold',
            color: 'var(--theme-text-secondary)',
            backgroundColor: '#f8f9fa'
        },
        defaultOption: {
            backgroundColor: '#e8f5e8',
            color: '#2d5a2d',
            fontWeight: 'bold'
        },
        defaultOptionDisabled: {
            backgroundColor: '#e8f5e8',
            color: 'var(--theme-text-muted)',
            fontWeight: 'bold',
            textDecoration: 'line-through'
        }
    };

    // Format the date as needed (e.g., "YYYY-MM-DD" or "MM/DD/YYYY")
    const formattedDate = currentDate.toLocaleDateString();
    const parseCurrency = (value) => {
        // Ensure the value is treated as a string before calling replace
        const stringValue = String(value || "0");

        // Remove the "R" symbol and trim any whitespace, then convert to a float
        return parseFloat(stringValue.replace("R", "").trim());
    };

    const isCatalogueDetachedLine = (item) => Boolean(item?.catalogue_detached || item?.detached_at);

    const overlayQuoteLineFromCatalogue = (item, priceListItem) => {
        const detached = isCatalogueDetachedLine(item);
        const meta = (!detached && priceListItem?.metadata) || {};
        const pli = detached ? null : priceListItem;
        const sn_hours_day = item.sn_hours_day ?? meta.sn_hours_day ?? pli?.sn_hours_day ?? 0;
        const sn_hours_night = item.sn_hours_night ?? meta.sn_hours_night ?? pli?.sn_hours_night ?? 0;
        const ena_hours_day = item.ena_hours_day ?? meta.ena_hours_day ?? pli?.ena_hours_day ?? 0;
        const ena_hours_night = item.ena_hours_night ?? meta.ena_hours_night ?? pli?.ena_hours_night ?? 0;
        const cw_hours_day = item.cw_hours_day ?? meta.cw_hours_day ?? pli?.cw_hours_day ?? 0;
        const cw_hours_night = item.cw_hours_night ?? meta.cw_hours_night ?? pli?.cw_hours_night ?? 0;
        const catering = item.catering ?? meta.catering ?? pli?.catering ?? 0;
        const cleaning = item.cleaning ?? meta.cleaning ?? pli?.cleaning ?? 0;
        const laundry = item.laundry ?? meta.laundry ?? pli?.laundry ?? 0;
        const other = item.other ?? meta.other ?? pli?.other ?? 0;
        return {
            ...item,
            catalogue_detached: detached,
            price_list_item_id: item.tenant_site_price_item_id || item.price_list_item_id,
            item_name: item.price_list_item_name || item.item_name,
            price: parseFloat(parseCurrency(item.unit_price || item.price)),
            id: item.quote_line_id || item.id,
            frequency: item.frequency || meta.frequency || pli?.frequency || '',
            sn_hours_day: parseFloat(sn_hours_day) || 0,
            sn_hours_night: parseFloat(sn_hours_night) || 0,
            ena_hours_day: parseFloat(ena_hours_day) || 0,
            ena_hours_night: parseFloat(ena_hours_night) || 0,
            cw_hours_day: parseFloat(cw_hours_day) || 0,
            cw_hours_night: parseFloat(cw_hours_night) || 0,
            catering: parseFloat(catering) || 0,
            cleaning: parseFloat(cleaning) || 0,
            laundry: parseFloat(laundry) || 0,
            other: parseFloat(other) || 0,
            description: item.description || meta.description || pli?.description || '',
        };
    };

    const lookupCatalogueItem = (item, priceListItemsMap) => {
        if (isCatalogueDetachedLine(item) || !priceListItemsMap) return null;
        const priceListItemId = item.price_list_item_key || item.tenant_site_price_item_id || item.price_list_item_id;
        return priceListItemsMap[priceListItemId] || null;
    };

    // Helper function to format currency with space as thousand separator and decimal point
    const formatCurrency = (value, decimals = 2) => {
      if (value === null || value === undefined || isNaN(value)) {
        return decimals === 1 ? '0.0' : '0.00';
      }
      
      const num = parseFloat(value);
      const parts = num.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join('.');
    };
  
    // Helper function to safely format numbers and prevent NaN
    const safeFormatNumber = (value, decimals = 2) => {
      if (value === null || value === undefined || isNaN(value)) {
        return decimals === 1 ? '0.0' : '0.00';
      }
      return parseFloat(value).toFixed(decimals);
    };

    const params = useParams();
    const id = customerIdProp ?? params.id;
    const dateCreated = dateCreatedProp ?? params.dateCreated;
    const Qid = quotationIdProp ?? params.Qid;
    const [Quotation, setQuotation] = useState(null);
    const [items, setItems] = useState([]);
    const [hasAddedItem, setHasAddedItem] = useState(false); // Track if user added items manually
    const [isAddingItems, setIsAddingItems] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);
    const [bulkRemoveLineItemIds, setBulkRemoveLineItemIds] = useState(new Set());
    const [defaultItemNames, setDefaultItemNames] = useState([]);

    const [lineItems, setLineItems] = useState([]);
    const [noItems, setNoItems] = useState(false);

    const [TotalsCarePlan, setTotalsCarePlan] = useState({
        sn_hours_day: 0,
        sn_hours_night: 0,
        ena_hours_day: 0,
        ena_hours_night: 0,
        cw_hours_day: 0,
        cw_hours_night: 0,
    });
    const [isRefreshingCarePlanTotals, setIsRefreshingCarePlanTotals] = useState(false);
    // Track which planRevision we last applied so we can prefetch on save and skip redundant tab-switch fetches
    const carePlanTotalsRevisionRef = useRef(-1);
    const activityMetaCacheRef = useRef({});

    const hourTotalsAreEmpty = (hours) => {
        if (!hours) return true;
        return !(
            Number(hours.sn_hours_day) ||
            Number(hours.sn_hours_night) ||
            Number(hours.ena_hours_day) ||
            Number(hours.ena_hours_night) ||
            Number(hours.cw_hours_day) ||
            Number(hours.cw_hours_night)
        );
    };

    // Prefer live plan hours from the shell so Quote matches Service Plan Overview exactly
    const displayPlanHours = !hourTotalsAreEmpty(planHourTotals)
        ? planHourTotals
        : TotalsCarePlan;

    const [totals, setTotals] = useState({
        price: 0,
        sn_hours_day: 0,
        sn_hours_night: 0,
        ena_hours_day: 0,
        ena_hours_night: 0,
        cw_hours_day: 0,
        cw_hours_night: 0,
        catering: 0,
        cleaning: 0,
        Labour: 0,
        laundry: 0,
        other: 0,
    });
    const [benefitsTotals, setbenefitsTotals] = useState({
        benefit_body: 0,
        benefit_mind: 0,
        benefit_social: 0
    });
    const [carePlanID, setCarePlanID] = useState(null);
    const [CustomerName, setCustomerName] = useState('');
    const [CustomerDiscount, setCustomerDiscount] = useState('');
    const [CustomerRoomNumber, setCustomerRoomNumber] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [pdfHeaderFill, setPdfHeaderFill] = useState('#356167');
    const [activities, setActivities] = useState([]);
    
    // State for tenant_site info (which tenant/site this quotation is linked to)
    const [quotationTenantSite, setQuotationTenantSite] = useState(null);
    const [collapsed, setCollapsed] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
    const [isSaved, setIsSaved] = useState(true);
    const [temp, setTemp] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [customerCareTypeId, setCustomerCareTypeId] = useState(null);
    const [defaultItems, setDefaultItems] = useState([]);
    const [sortedItems, setSortedItems] = useState([]);
    const [isActivating, setIsActivating] = useState(false);
    const [edit, setEdit] = useState(false);
    const [loadingQuotation, setLoadingQuotation] = useState(true);

    // Report edit/dirty state to the shared Service Plan / Quote shell
    useEffect(() => {
        if (!onEditStateChange) return;
        onEditStateChange({ editing: edit, dirty: edit && !isSaved });
    }, [edit, isSaved, onEditStateChange]);

    // States for copy from previous quotation modal
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [availableQuotations, setAvailableQuotations] = useState([]);
    const [selectedQuotationId, setSelectedQuotationId] = useState('');
    const [selectedQuotationLineItems, setSelectedQuotationLineItems] = useState([]);
    const [selectedLineItemIds, setSelectedLineItemIds] = useState([]);
    const [loadingQuotations, setLoadingQuotations] = useState(false);
    const [loadingLineItems, setLoadingLineItems] = useState(false);

    const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null, confirmLabel: 'Confirm', confirmVariant: 'danger' });
    const showToast = (message, variant = 'success') => setToast({ show: true, message, variant });
    const showConfirm = (message, onConfirm, { title = 'Confirm', confirmLabel = 'Confirm', confirmVariant = 'danger' } = {}) =>
        setConfirmModal({ show: true, title, message, onConfirm, confirmLabel, confirmVariant });
    useEffect(() => {
        if (!toast.show) return;
        const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
        return () => clearTimeout(timer);
    }, [toast.show]);

    // Report quote hour totals to the shell sticky labour-difference bar (plan hours come from CarePlanDetail)
    useEffect(() => {
        if (typeof onHourTotalsChange !== 'function') return;
        onHourTotalsChange('quote', {
            sn_hours_day: (Number(totals.sn_hours_day) || 0) / 60,
            sn_hours_night: (Number(totals.sn_hours_night) || 0) / 60,
            ena_hours_day: (Number(totals.ena_hours_day) || 0) / 60,
            ena_hours_night: (Number(totals.ena_hours_night) || 0) / 60,
            cw_hours_day: (Number(totals.cw_hours_day) || 0) / 60,
            cw_hours_night: (Number(totals.cw_hours_night) || 0) / 60,
        });
    }, [totals, onHourTotalsChange]);

    const setSelectedCustomerData = () => {
        if (!Quotation) return;

        const customerData = {
            CustomerName: CustomerName || "Unknown",
            QuotationID: Quotation?.quotation_id || '',
            RoomNumber: CustomerRoomNumber || '',
            ReportDate: new Date().toLocaleDateString(),
            TotalMonthlyFee: total.toFixed(2),
            CustomerDiscount: CustomerDiscount || 0,
            FinalQuotedPrice: (total - ((total * CustomerDiscount) / 100)).toFixed(2),
            CarePlanActivities: activities.length > 0 ? activities : ["No Care Plan activities found"],
            QuotedServices: lineItems.map(item => ({
                name: item.item_name,
                fee: (parseCurrency(item.price) * getMultiplier(item.multiplier)).toFixed(2)
            })),
            AdditionalNotes: notes
        };

        return customerData;
    };

    const convertImageToBase64 = async (imageUrl) => {
        try {
            let response;
            if (typeof imageUrl === 'string' && (imageUrl.startsWith('blob:') || imageUrl.startsWith('data:'))) {
                response = await fetch(imageUrl);
            } else if (typeof imageUrl === 'string' && imageUrl.includes('/tenant-branding/logo')) {
                const qs = currentTenant?.id
                    ? `?tenant_id=${encodeURIComponent(currentTenant.id)}`
                    : '';
                response = await fetchWithAuth(`${API_BASE_URL}/tenant-branding/logo${qs}`);
            } else {
                response = await fetchWithAuth(`${API_BASE_URL}/proxy-image`, {
                    method: 'POST',
                    body: JSON.stringify({ imageUrl })
                });
            }

            if (!response.ok) {
                throw new Error('Failed to fetch image');
            }

            const blob = await response.blob();

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error converting image to base64:', error);
            return null;
        }
    };

    const handleCreateQuotepdf = async () => {
        setIsDownloading(true);
        try {
            const quotationData = setSelectedCustomerData();

            if (!quotationData) {
                showToast("Quotation data is not available.", 'warning');
                return;
            }

            // Get signature data from form fields
            const signatureData = {
                clientName: document.getElementById('clientSignatureName')?.value || '',
                clientDate: document.getElementById('clientSignatureDate')?.value || '',
                companyName: document.getElementById('companySignatureName')?.value || '',
                companyDate: document.getElementById('companySignatureDate')?.value || ''
            };

            // Add signature data to quotationData
            quotationData.signatures = {
                clientName: signatureData.clientName.trim(),
                clientDate: signatureData.clientDate.trim(),
                companyName: signatureData.companyName.trim(),
                companyDate: signatureData.companyDate.trim()
            };

            const pieCharts = Array.from(document.querySelectorAll('#summary-details .chart-item'));

            // Convert logo to base64 if available
            let base64Logo = null;
            if (logoUrl) {
                try {
                    base64Logo = await convertImageToBase64(logoUrl);
                    if (!base64Logo) {
                        console.warn('Failed to convert logo to base64, proceeding without logo');
                    }
                } catch (error) {
                    console.error('Error processing logo:', error);
                }
            }

            await generateQuotationPDF(quotationData, pieCharts, base64Logo, null, pdfHeaderFill);
        } catch (error) {
            console.error('Error generating PDF:', error);
            showToast('Failed to generate PDF. Please try again.', 'danger');
        } finally {
            setIsDownloading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        let objectUrlToRevoke = null;

        const loadTenantBranding = async () => {
            if (!currentTenant?.id) {
                if (!cancelled) {
                    setLogoUrl('');
                    setPdfHeaderFill('#356167');
                }
                return;
            }

            // Clear previous tenant branding while loading the next
            if (!cancelled) {
                setLogoUrl('');
                setPdfHeaderFill('#356167');
            }

            const qs = `?tenant_id=${encodeURIComponent(currentTenant.id)}`;

            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/tenant-branding${qs}`);

                if (!response.ok) {
                    console.error(`HTTP error! Status: ${response.status}`);
                    if (!cancelled) {
                        setLogoUrl('');
                        setPdfHeaderFill('#356167');
                    }
                    return;
                }

                const branding = await response.json();
                if (cancelled) return;

                setPdfHeaderFill(branding.headerFill || '#356167');

                if (branding.hasLogo) {
                    const logoResponse = await fetchWithAuth(`${API_BASE_URL}/tenant-branding/logo${qs}`);
                    if (cancelled) return;
                    if (logoResponse.ok) {
                        const blob = await logoResponse.blob();
                        const objectUrl = URL.createObjectURL(blob);
                        if (cancelled) {
                            URL.revokeObjectURL(objectUrl);
                            return;
                        }
                        objectUrlToRevoke = objectUrl;
                        setLogoUrl(objectUrl);
                    } else {
                        setLogoUrl('');
                    }
                } else {
                    setLogoUrl('');
                }
            } catch (error) {
                console.error('Error fetching tenant branding:', error);
                if (!cancelled) setLogoUrl('');
            }
        };

        loadTenantBranding();
        return () => {
            cancelled = true;
            if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
        };
    }, [currentTenant?.id]);
    const handleSort = (key, isNumeric = false) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }

        const sortedData = [...lineItems].sort((a, b) => {
            if (isNumeric) {
                return direction === 'ascending'
                    ? (a[key] || 0) - (b[key] || 0)
                    : (b[key] || 0) - (a[key] || 0);
            } else {
                return direction === 'ascending'
                    ? (a[key] || '').toString().localeCompare((b[key] || '').toString())
                    : (b[key] || '').toString().localeCompare((a[key] || '').toString());
            }
        });

        setSortConfig({ key, direction });
        setLineItems(sortedData);
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? <FaAngleUp /> : <FaAngleDown />;
        }
        return null;
    };

    // Helper function to sort items: defaults first, then alphabetically
    const sortItemsWithDefaultsFirst = (items) => {
        return [...items].sort((a, b) => {
            const aIsDefault = defaultItems.some(def =>
                String(def) === String(a.price_list_item_id) ||
                String(def) === String(a.tenant_site_price_item_id) ||
                String(def) === String(a.price_list_item_key)
            );
            const bIsDefault = defaultItems.some(def =>
                String(def) === String(b.price_list_item_id) ||
                String(def) === String(b.tenant_site_price_item_id) ||
                String(def) === String(b.price_list_item_key)
            );

            if (aIsDefault && !bIsDefault) return -1;
            if (!aIsDefault && bIsDefault) return 1;

            return (a.item_name || '').localeCompare(b.item_name || '');
        });
    };

    const handleSearchChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
    };

    const filteredItems = lineItems.filter(item =>
        item.item_name?.toLowerCase().includes(searchTerm) ||
        item.description?.toLowerCase().includes(searchTerm)
    );
    const [hourlyRates, setHourlyRates] = useState({ SN: 0, ENA: 0, CW: 0 });

    const fetchHourlyRates = async (dateCreated) => {
        try {
            // Use snake_case for query parameter to match backend expectation
            const tenantParam = currentTenant?.id
                ? `&tenant_id=${encodeURIComponent(currentTenant.id)}`
                : '';
            const response = await fetchWithAuth(
                `${API_BASE_URL}/staff/rates?date_created=${encodeURIComponent(dateCreated || '')}${tenantParam}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Check if the response is JSON
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error("Received non-JSON response");
            }

            const ratesArray = await response.json();

            // Convert the array to an object with SN, ENA, CW keys
            // Handle both PascalCase (StaffType, StandardHourlyprice) and snake_case (staff_type, standard_hourly_price)
            const mappedRates = ratesArray.reduce((acc, rate) => {
                const staffType = rate.StaffType || rate.staff_type || rate.flow_staff_type;
                const hourlyPrice = rate.StandardHourlyprice || rate.standard_hourly_price;
                if (staffType) {
                    acc[staffType] = parseFloat(hourlyPrice) || 0;
                }
                return acc;
            }, { SN: 0, ENA: 0, CW: 0 });

            setHourlyRates(mappedRates);
        } catch (error) {
            console.error('Error fetching hourly rates:', error);
            // Set default rates to avoid breaking calculations
            setHourlyRates({ SN: 0, ENA: 0, CW: 0 });
        }
    };



    useEffect(() => {
        fetchHourlyRates(dateCreated || new Date().toISOString().split('T')[0]);
    }, [currentTenant?.id, dateCreated]);
    const calculateTotals = (items, rates) => {
        return items.reduce((totals, item) => {
            const multiplier = getMultiplier(item.multiplier);
            const newSnDay = totals.sn_hours_day + (parseFloat(item.sn_hours_day || 0) * multiplier);
            const newSnNight = totals.sn_hours_night + (parseFloat(item.sn_hours_night || 0) * multiplier);
            const newEnaDay = totals.ena_hours_day + (parseFloat(item.ena_hours_day || 0) * multiplier);
            const newEnaNight = totals.ena_hours_night + (parseFloat(item.ena_hours_night || 0) * multiplier);
            const newCwDay = totals.cw_hours_day + (parseFloat(item.cw_hours_day || 0) * multiplier);
            const newCwNight = totals.cw_hours_night + (parseFloat(item.cw_hours_night || 0) * multiplier);

            const totalLabourCost =
                ((newSnDay + newSnNight) / 60) * rates.SN +
                ((newEnaDay + newEnaNight) / 60) * rates.ENA +
                ((newCwDay + newCwNight) / 60) * rates.CW;

            return {
                price: totals.price + (parseFloat(parseCurrency(item.price) || 0)),
                sn_hours_day: newSnDay,
                sn_hours_night: newSnNight,
                ena_hours_day: newEnaDay,
                ena_hours_night: newEnaNight,
                cw_hours_day: newCwDay,
                cw_hours_night: newCwNight,
                catering: totals.catering + parseFloat(parseCurrency(item.catering) || 0),
                cleaning: totals.cleaning + parseFloat(parseCurrency(item.cleaning) || 0),
                laundry: totals.laundry + parseFloat(parseCurrency(item.laundry) || 0),
                other: totals.other + parseFloat(parseCurrency(item.other) || 0),
                Labour: totalLabourCost,
            };
        }, {
            sn_hours_day: 0,
            sn_hours_night: 0,
            ena_hours_day: 0,
            ena_hours_night: 0,
            cw_hours_day: 0,
            cw_hours_night: 0,
            catering: 0,
            cleaning: 0,
            laundry: 0,
            other: 0,
            Labour: 0,
        });
    };


    // Debug: Track effect triggers
    useEffect(() => {
        const fetchQuotation = async () => {
            // Validate all required parameters before making API call
            // Only skip if ALL parameters are missing (initial load before React Router populates params)
            // If at least one parameter exists, try to fetch - the API will return appropriate error
            const hasValidId = id && id !== 'undefined' && id !== 'null' && id.trim() !== '';
            const hasValidDate = dateCreated && dateCreated !== 'undefined' && dateCreated !== 'null' && dateCreated.trim() !== '';
            const hasValidQid = Qid && Qid !== 'undefined' && Qid !== 'null' && Qid.trim() !== '';
            
            // Only skip if we don't have any valid parameters (React Router hasn't populated them yet)
            if (!hasValidId && !hasValidDate && !hasValidQid) {
                console.warn('Waiting for URL parameters to load...');
                return;
            }
            
            // If we have at least the Qid (quotation ID), try to fetch
            // The API will handle validation and return appropriate errors
            if (!hasValidQid) {
                console.warn('Missing Qid parameter, cannot fetch quotation');
                setQuotation({ Error: 'Missing quotation ID', QuotationID: null });
                return;
            }
            
            try {
                // Use the new API endpoint to get quotation with line items
                const response = await fetchWithAuth(`${API_BASE_URL}/quotations/${Qid}`);
                if (response.ok) {
                    const data = await response.json();
                    
                    // Handle both PascalCase (Quotation) and snake_case (quotation) from API
                    // The new API returns: { quotation: {...}, line_items: [...] }
                    const quotationData = data.quotation || data.Quotation;
                    setQuotation(quotationData);
                    setLoadingQuotation(false);

                    // Extract CarePlanID from the response if available
                    const initialCarePlanId = quotationData?.care_plan_id || quotationData?.CarePlanId || quotationData?.carePlanId;
                    if (initialCarePlanId) {
                        setCarePlanID(initialCarePlanId);
                    }

                    // Extract customer_care_type_id from quotation data (captured at quotation creation time)
                    // This is used to determine default items for the quotation
                    // Priority: quotation's care type > customer's care type
                    const quotationCareTypeId = quotationData?.customer_care_type_id || quotationData?.CustomerCareTypeID;
                    if (quotationCareTypeId) {
                        setCustomerCareTypeId(quotationCareTypeId);
                    }

                    // Extract tenant_site_id from quotation data
                    const tenantSiteId = quotationData?.tenant_site_id || quotationData?.TenantSiteID;
                    if (tenantSiteId) {
                        setQuotationTenantSite({ tenant_site_id: tenantSiteId });
                    }

                    // Handle both camelCase (lineItems) and snake_case (line_items) from API
                    const lineItemsData = data.line_items || data.lineItems || data.line_items;

                    // Check if this is a new quotation with no line items
                    if (!lineItemsData || lineItemsData.length === 0) {
                        setLineItems([]);
                        setTemp([]);
                        setTotals({
                            price: 0,
                            sn_hours_day: 0,
                            sn_hours_night: 0,
                            ena_hours_day: 0,
                            ena_hours_night: 0,
                            cw_hours_day: 0,
                            cw_hours_night: 0,
                            catering: 0,
                            cleaning: 0,
                            Labour: 0,
                            laundry: 0,
                            other: 0,
                        });
                    } else {
                        // For price list items, we need to fetch the full price list item details
                        // to get the hours from metadata. First, let's get all price list items.
                        let priceListItemsMap = {};
                        try {
                            // Use tenant_site_id from the quotation itself, not from state (which may not be set yet)
                            const tenantSiteIdFromQuote = quotationData?.tenant_site_id;
                            const tenantIdParam = tenantSiteIdFromQuote
                                ? `&tenant_site_id=${tenantSiteIdFromQuote}`
                                : (currentTenant?.id ? `&tenant_id=${currentTenant.id}` : '');
                            const priceListResponse = await fetchWithAuth(`${API_BASE_URL}/pricelistItems?customer_id=${id}${tenantIdParam}`);
                            if (priceListResponse.ok) {
                                const priceListData = await priceListResponse.json();
                                if (Array.isArray(priceListData)) {
                                    // Create a map keyed by both price_list_item_id and price_list_item_key
                                    priceListData.forEach(pli => {
                                        // Map by numeric ID if exists
                                        if (pli.price_list_item_id) {
                                            priceListItemsMap[pli.price_list_item_id] = pli;
                                        }
                                        // Map by string key if exists (for tenant-scoped items)
                                        if (pli.price_list_item_key) {
                                            priceListItemsMap[pli.price_list_item_key] = pli;
                                        }
                                    });
                                }
                            }
                        } catch (e) {
                            console.error('Failed to fetch price list items for mapping:', e);
                        }
                        
                        // Map the new API response to our format
                        // Priority: 1) Direct item fields 2) Price list metadata 3) Price list top-level fields 4) Default to 0
                        const mappedLineItems = lineItemsData.map(item =>
                            overlayQuoteLineFromCatalogue(item, lookupCatalogueItem(item, priceListItemsMap))
                        );
                        
                        // Sort line items: defaults first, then alphabetically
                        const sortedLineItems = sortItemsWithDefaultsFirst(mappedLineItems);
                        
                        
                        setLineItems(sortedLineItems);
                        setTemp(sortedLineItems);
                        
                        // DEBUG: Log the totals calculation
                        const calculatedTotals = calculateTotals(mappedLineItems, hourlyRates);
                        setTotals(calculatedTotals);
                        
                    }

                } else if (response.status === 404) {
                    console.error('Quotation not found (404)');
                    // Try to get more details from response
                    try {
                        const errorText = await response.text();
                        console.error('Error response body:', errorText);
                    } catch (e) {
                        console.error('Could not read error response');
                    }
                    // Set empty quotation to exit loading state and show error
                    setQuotation({ Error: 'Not Found', QuotationID: Qid });
                    setLoadingQuotation(false);

                } else {
                    console.error('Failed to fetch quotation, status:', response.status);
                    const errorText = await response.text();
                    console.error('Error response:', errorText);
                    setQuotation({ Error: `Server error: ${response.status}`, QuotationID: Qid });
                    setLoadingQuotation(false);
                }
            } catch (error) {
                console.error('An error occurred while fetching the Quotation:', error);
                setQuotation({ Error: error.message, QuotationID: Qid });
                setLoadingQuotation(false);
            }
        };



        const fetchItems = async (tenantSiteId = null) => {
            try {
                // Use provided tenant_site_id or fallback to current tenant
                const tenantIdParam = tenantSiteId
                    ? `&tenant_site_id=${tenantSiteId}`
                    : (currentTenant?.id ? `&tenant_id=${currentTenant.id}` : '');
                
                // Fetch all items - filter by tenant_site_id (or tenant_id as fallback) and customer_id
                const allItemsResponse = await fetchWithAuth(`${API_BASE_URL}/pricelistItems?customer_id=${id}${tenantIdParam}`);
                
                // Debug log to check response
                const allItemsData = await allItemsResponse.json();


                if (Array.isArray(allItemsData)) {
                    // --- Care type and date filtering ---
                    const today = new Date();
                    const quotationCareTypeId = String(Quotation?.customer_care_type_id || Quotation?.CustomerCareTypeID || customerCareTypeId || '');
                    const filteredItems = allItemsData.filter(item => {
                        // All items are shown regardless of care type.
                        // Care type only determines whether an item is "default" or "normal".

                        // Date match: treat null/empty/1899-11-23 as always valid
                        const isNoDate = (d) => {
                            if (!d) return true;
                            const dt = new Date(d);
                            // 1899-11-23 is a common Excel/legacy default date
                            return dt.getFullYear() === 1899 && dt.getMonth() === 10 && dt.getDate() === 23;
                        };
                        const fromValid = isNoDate(item.date_from) || today >= new Date(item.date_from);
                        const toValid = isNoDate(item.date_to) || today <= new Date(item.date_to);

                        return fromValid && toValid;
                    });

                    // Fetch default items for the quotation's care type if available
                    let defaultItemsData = [];
                    if (quotationCareTypeId) {
                        try {
                            const defaultTenantIdParam = tenantSiteId
                                ? `&tenant_site_id=${tenantSiteId}`
                                : (currentTenant?.id ? `&tenant_id=${currentTenant.id}` : '');
                            const defaultItemsResponse = await fetchWithAuth(`${API_BASE_URL}/pricelistItems?customer_care_type_id=${quotationCareTypeId}&customer_id=${id}${defaultTenantIdParam}`);
                            const defaultData = await defaultItemsResponse.json();
                            if (Array.isArray(defaultData)) {
                                defaultItemsData = defaultData;
                            }
                        } catch (error) {
                            console.error('Failed to fetch default items:', error);
                        }
                    }

                    // Get IDs and names of default items — union of API results and local care-type check
                    // (backend may not handle comma-separated customer_care_type_id in query params)
                    const apiDefaultIds = defaultItemsData.map(item => item.price_list_item_id);
                    const apiDefaultNames = defaultItemsData.map(item => item.item_name).filter(Boolean);
                    const localDefaultItems = quotationCareTypeId
                        ? filteredItems
                            .filter(item => {
                                const careTypeRaw = (item.customer_care_type_id || '').trim();
                                if (!careTypeRaw) return false;
                                return careTypeRaw.split(',').map(s => s.trim()).filter(Boolean).includes(quotationCareTypeId);
                            })
                        : [];
                    const localDefaultIds = localDefaultItems.map(item => item.price_list_item_id);
                    const localDefaultNames = localDefaultItems.map(item => item.item_name).filter(Boolean);
                    const defaultItemIds = [...new Set([...apiDefaultIds, ...localDefaultIds])];
                    const allDefaultNames = [...new Set([...apiDefaultNames, ...localDefaultNames])];
                    setDefaultItems(defaultItemIds);
                    setDefaultItemNames(allDefaultNames);

                    // Sort items: defaults first, then alphabetically
                    const sortedData = filteredItems.sort((a, b) => {
                        const aIsDefault = defaultItemIds.includes(a.price_list_item_id);
                        const bIsDefault = defaultItemIds.includes(b.price_list_item_id);

                        if (aIsDefault && !bIsDefault) return -1;
                        if (!aIsDefault && bIsDefault) return 1;

                        // Safely handle undefined/null item_name values
                        const nameA = a.item_name || '';
                        const nameB = b.item_name || '';
                        return nameA.localeCompare(nameB);
                    });

                    setItems(sortedData);
                    setSortedItems(sortedData);
                } else {
                    console.error('Unexpected data format for Items:', allItemsData);
                    setItems([]);
                    setSortedItems([]);
                }

                if (allItemsData.length === 0) {
                    setNoItems(true);
                }
            } catch (error) {
                console.error('Failed to fetch Items:', error);
                setItems([]);
                setSortedItems([]);
            }
        };

        
        if (hasAddedItem || (lineItems.length > 0 && Quotation?.quotation_id)) {
            setLoadingQuotation(false);
            return;
        }
        
        // Reset loading state when URL parameters change
        setLoadingQuotation(true);
        
        // Fetch quotation first to get tenant_site_id, then fetch items with correct filter
        const fetchQuotationAndItems = async () => {
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/quotations/${Qid}`);
                if (response.ok) {
                    const data = await response.json();
                    const quotationData = data.quotation || data.Quotation;
                    setQuotation(quotationData);
                    setLoadingQuotation(false);

                    const carePlanIdFromQuotation = quotationData?.care_plan_id || quotationData?.CarePlanId || quotationData?.carePlanId;
                    if (carePlanIdFromQuotation) {
                        setCarePlanID(carePlanIdFromQuotation);
                    }

                    // Extract tenant_site_id from quotation data
                    const tenantSiteId = quotationData?.tenant_site_id || quotationData?.TenantSiteID;
                    if (tenantSiteId) {
                        setQuotationTenantSite({ tenant_site_id: tenantSiteId });
                    }

                    const serverLineItems = data.line_items || data.lineItems || [];
                    // Quick Quote (and POST /quotations) already persist defaults. Mark that
                    // before loading the catalogue so the client-side default injector cannot
                    // race and duplicate line items.
                    if (serverLineItems.length > 0) {
                        setHasAddedItem(true);
                    }

                    await fetchItems(tenantSiteId);

                    if (serverLineItems.length === 0) {
                        setLineItems([]);
                        setTemp([]);
                        setTotals({
                            price: 0,
                            sn_hours_day: 0,
                            sn_hours_night: 0,
                            ena_hours_day: 0,
                            ena_hours_night: 0,
                            cw_hours_day: 0,
                            cw_hours_night: 0,
                            catering: 0,
                            cleaning: 0,
                            Labour: 0,
                            laundry: 0,
                            other: 0,
                        });
                    } else {
                        // For price list items, we need to fetch the full price list item details
                        let priceListItemsMap = {};
                        try {
                            const tenantSiteIdFromQuote = quotationData?.tenant_site_id;
                            const tenantIdParam = tenantSiteIdFromQuote
                                ? `&tenant_site_id=${tenantSiteIdFromQuote}`
                                : (currentTenant?.id ? `&tenant_id=${currentTenant.id}` : '');
                            const priceListResponse = await fetchWithAuth(`${API_BASE_URL}/pricelistItems?customer_id=${id}${tenantIdParam}`);
                            if (priceListResponse.ok) {
                                const priceListData = await priceListResponse.json();
                                if (Array.isArray(priceListData)) {
                                    priceListData.forEach(pli => {
                                        if (pli.price_list_item_id) {
                                            priceListItemsMap[pli.price_list_item_id] = pli;
                                        }
                                        if (pli.price_list_item_key) {
                                            priceListItemsMap[pli.price_list_item_key] = pli;
                                        }
                                    });
                                }
                            }
                        } catch (e) {
                            console.error('Failed to fetch price list items for mapping:', e);
                        }
                        
                        const mappedLineItems = data.line_items.map(item =>
                            overlayQuoteLineFromCatalogue(item, lookupCatalogueItem(item, priceListItemsMap))
                        );
                        
                        const sortedLineItems = sortItemsWithDefaultsFirst(mappedLineItems);
                        setLineItems(sortedLineItems);
                        setTemp(sortedLineItems);
                        
                        const calculatedTotals = calculateTotals(mappedLineItems, hourlyRates);
                        setTotals(calculatedTotals);
                    }
                } else if (response.status === 404) {
                    console.error('Quotation not found (404)');
                    try {
                        const errorText = await response.text();
                        console.error('Error response body:', errorText);
                    } catch (e) {
                        console.error('Could not read error response');
                    }
                    setQuotation({ Error: 'Not Found', QuotationID: Qid });
                    setLoadingQuotation(false);
                } else {
                    console.error('Failed to fetch quotation, status:', response.status);
                    const errorText = await response.text();
                    console.error('Error response:', errorText);
                    setQuotation({ Error: `Server error: ${response.status}`, QuotationID: Qid });
                    setLoadingQuotation(false);
                }
            } catch (error) {
                console.error('An error occurred while fetching the Quotation:', error);
                setQuotation({ Error: error.message, QuotationID: Qid });
                setLoadingQuotation(false);
            }
        };
        
        fetchQuotationAndItems();


    }, [id, dateCreated, Qid, customerCareTypeId, currentTenant?.id, quotationTenantSite?.tenant_site_id, hasAddedItem, Quotation?.quotation_id]);

    // Add default items to new quotations after items are loaded
    // Only run once by tracking if default items have been added
    useEffect(() => {
        // Skip if user has manually added items
        if (hasAddedItem) {
            return;
        }
        
        // Skip if we already have line items from the API (existing quotation)
        // This prevents adding defaults to already-saved quotations
        if (lineItems.length > 0 && Quotation?.quotation_id) {
            return;
        }
        
        if (items.length > 0 && defaultItems.length > 0) {
            const addDefaultItemsToNewQuotation = async () => {
                try {
                    // Get the default items from the items list
                    const defaultItemsToAdd = items.filter(item => defaultItems.includes(item.price_list_item_id));

                    if (defaultItemsToAdd.length > 0) {
                        // Create line items from default items
                        const newLineItems = defaultItemsToAdd.map(item => {
                            // Check if this is a string-based key (from tenant_site filtered items)
                            const priceListItemId = item.price_list_item_id;
                            const isStringKey = isNaN(parseInt(priceListItemId));
                            
                            // Create the line item with proper ID mapping
                            const lineItem = {
                                item_name: item.item_name,
                                price: item.price,
                                CustomerCareTypeID: item.CustomerCareTypeID,
                                general_category: item.general_category,
                                frequency: item.frequency,
                                multiplier: 1, // Default multiplier is 1
                                sn_hours_day: item.sn_hours_day || '0',
                                sn_hours_night: item.sn_hours_night || '0',
                                ena_hours_day: item.ena_hours_day || '0',
                                ena_hours_night: item.ena_hours_night || '0',
                                cw_hours_day: item.cw_hours_day || '0',
                                cw_hours_night: item.cw_hours_night || '0',
                                catering: item.catering || '0',
                                cleaning: item.cleaning || '0',
                                laundry: item.laundry || '0',
                                other: item.other || '0',
                                date_from: item.date_from || '',
                                date_to: item.date_to || '',
                            };
                            
                            // Store the original price_list_item_id for display
                            lineItem.price_list_item_id = priceListItemId;
                            
                            // If string key, also store as price_list_item_key for backend
                            if (isStringKey) {
                                lineItem.price_list_item_key = priceListItemId;
                            }
                            
                            return lineItem;
                        });

                        // Set the line items (sorted)
                        const sortedLineItems = sortItemsWithDefaultsFirst(newLineItems);
                        setLineItems(sortedLineItems);
                        setTemp(sortedLineItems);

                        // Calculate totals
                        const calculatedTotals = calculateTotals(newLineItems, hourlyRates);
                        setTotals(calculatedTotals);

                        // Save the quotation with default items
                        await saveQuotationWithLineItems(newLineItems);
                    }
                } catch (error) {
                    console.error('Error adding default items to new quotation:', error);
                }
            };

            const saveQuotationWithLineItems = async (lineItemsToSave) => {
                // Validate required parameters - allow empty string but not undefined/null
                const validQid = Qid && Qid !== 'undefined' && Qid !== 'null';
                
                if (!validQid) {
                    console.warn('Missing required Qid for saveQuotationWithLineItems:', { Qid });
                    return;
                }
                
                try {
                    // Add each default item using the new API - wait for all to complete
                    const savePromises = lineItemsToSave.map(async (item) => {
                        const priceListItemId = item.price_list_item_id;
                        const isStringKey = isNaN(parseInt(priceListItemId));
                        
                        // For string keys (like "legacy_price_item_42"), we should NOT extract the numeric ID
                        // and send it as tenant_site_price_item_id, as the backend will interpret it as
                        // a global db_id instead of a tenant-scoped ID.
                        // Instead, we send price_list_item_key with tenant_site_id for proper scoping.
                        // For numeric IDs, we send tenant_site_price_item_id directly.
                        const payload = {
                            ...(!isStringKey && { tenant_site_price_item_id: parseInt(priceListItemId, 10) }),
                            ...(isStringKey && { price_list_item_key: priceListItemId }),
                            ...(isStringKey && { tenant_site_id: quotationTenantSite?.tenant_site_id }),
                            multiplier: getMultiplier(item.multiplier),
                            sn_hours_day: parseFloat(item.sn_hours_day || 0),
                            sn_hours_night: parseFloat(item.sn_hours_night || 0),
                            ena_hours_day: parseFloat(item.ena_hours_day || 0),
                            ena_hours_night: parseFloat(item.ena_hours_night || 0),
                            cw_hours_day: parseFloat(item.cw_hours_day || 0),
                            cw_hours_night: parseFloat(item.cw_hours_night || 0),
                            catering: parseFloat(parseCurrency(item.catering) || 0),
                            cleaning: parseFloat(parseCurrency(item.cleaning) || 0),
                            laundry: parseFloat(parseCurrency(item.laundry) || 0),
                            other: parseFloat(parseCurrency(item.other) || 0),
                            frequency: item.frequency || 'Daily',
                        };
                        
                        const response = await fetchWithAuth(`${API_BASE_URL}/quotations/${Qid}/line-items`, {
                            method: 'POST',
                            body: JSON.stringify(payload)
                        });

                        if (!response.ok) {
                            const errorText = await response.text();
                            console.error('Failed to save default item:', item.item_name, 'Error:', errorText);
                            return { success: false, item: item.item_name };
                        }
                        
                        const result = await response.json();
                        return { success: true, item: item.item_name, result };
                    });
                    
                    const results = await Promise.all(savePromises);
                    const failedCount = results.filter(r => !r.success).length;
                    
                    if (failedCount > 0) {
                        console.warn(`${failedCount} default items failed to save`);
                    } else {
                        // After saving, mark as having items so we don't add defaults again
                        setHasAddedItem(true);
                        
                        // After saving, refresh the quotation to get the saved items from the backend
                        // This ensures the line items have proper IDs for future operations
                        try {
                            const refreshResponse = await fetchWithAuth(`${API_BASE_URL}/quotations/${Qid}`);
                            if (refreshResponse.ok) {
                                const data = await refreshResponse.json();
                                const lineItemsData = data.line_items || data.lineItems || [];
                                
                                // Map saved line items using the same three-level hour fallback
                                // as the main fetch: saved field → price list metadata → price list top-level → 0
                                const mappedLineItems = lineItemsData.map(item => {
                                    const pliKey = item.price_list_item_key || item.tenant_site_price_item_id || item.price_list_item_id;
                                    const priceListItem = isCatalogueDetachedLine(item) ? null : items.find(i =>
                                        i.price_list_item_id === pliKey ||
                                        i.price_list_item_key === pliKey ||
                                        String(i.price_list_item_id) === String(pliKey)
                                    );
                                    return overlayQuoteLineFromCatalogue(item, priceListItem);
                                });
                                
                                const sortedLineItems = sortItemsWithDefaultsFirst(mappedLineItems);
                                setLineItems(sortedLineItems);
                                setTemp(sortedLineItems);
                            }
                        } catch (refreshError) {
                            console.error('Failed to refresh line items after save:', refreshError);
                        }
                    }
                } catch (error) {
                    console.error('Error saving default items to quotation:', error);
                }
            };

            addDefaultItemsToNewQuotation();
        }
    }, [items, defaultItems, lineItems, id, dateCreated, Qid, hourlyRates, hasAddedItem]);
    useEffect(() => {
        const normalizeActivityId = (rawId) => {
            if (rawId == null) return null;
            const s = String(rawId);
            if (s.startsWith('legacy_activity_')) return s.slice('legacy_activity_'.length);
            if (s.startsWith('activity_')) return s.slice('activity_'.length);
            return s;
        };

        const fetchActivities = async () => {
            const carePlanId = Quotation?.care_plan_id || Quotation?.CarePlanId || Quotation?.carePlanId;
            if (!carePlanId || !id) return;

            try {
                const [carePlanResponse, activitiesResponse] = await Promise.all([
                    fetchWithAuth(`${API_BASE_URL}/careplans/${id}/${carePlanId}`),
                    fetchWithAuth(`${API_BASE_URL}/careitemactivities?customer_id=${id}`),
                ]);

                if (carePlanResponse.status === 404) {
                    setActivities([]);
                    return;
                }
                if (!carePlanResponse.ok) {
                    console.error(`Failed to fetch activities: ${carePlanResponse.status} ${carePlanResponse.statusText}`);
                    return;
                }

                const carePlanData = await carePlanResponse.json();
                const carePlanLineItems = carePlanData.lineItems || carePlanData.line_items || [];

                let activitiesMap = {};
                if (activitiesResponse.ok) {
                    const activitiesData = await activitiesResponse.json();
                    if (Array.isArray(activitiesData)) {
                        activitiesData.forEach(a => {
                            const norm = normalizeActivityId(a.activity_id ?? a.ActivityID);
                            if (norm) activitiesMap[norm] = a.activity_name || a.ActivityName || '';
                        });
                    }
                }

                const names = carePlanLineItems
                    .map(item => {
                        const directName = item.activity_name || item.ActivityName;
                        if (directName) return directName;
                        const norm = normalizeActivityId(item.activity_id ?? item.activity_key);
                        return (norm && activitiesMap[norm]) || null;
                    })
                    .filter(Boolean);

                setActivities(names);
            } catch (error) {
                console.error('An error occurred while fetching the activities:', error);
            }
        };

        if (Quotation && !Quotation.Error) {
            fetchActivities();
        }
    }, [Quotation, id]);

    const fetchCustomerDetails = async (customerId) => {
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/care-recipients/${customerId}`);

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Check if the response is JSON
            const contentType = response.headers.get("content-type");
            if (!contentType || !contentType.includes("application/json")) {
                throw new Error("Received non-JSON response");
            }

            const data = await response.json();

            // Assuming the response JSON object has 'name', 'discount', and 'customer_care_type_id' fields
            setCustomerName(`${data.name} ${data.surname}`);
            setCustomerDiscount(data.discount);
            setCustomerCareTypeId(data.customer_care_type_id);
            // Extract room number from the database field
            setCustomerRoomNumber(data.room_number || '');
        } catch (error) {
            console.error('Error fetching Customer:', error);
        }
    };

    // Fetch customer details on component mount
    useEffect(() => {
        const customerId = id; // Replace with actual customer ID or prop
        fetchCustomerDetails(customerId);
    }, []);


    // Helper function to fetch tenant name
    const fetchTenantName = async (tenantId) => {
        if (!tenantId) return null;
        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/my-tenants`);
            if (response.ok) {
                const data = await response.json();
                const tenant = data.find(t =>
                    String(t.tenant_id) === String(tenantId) ||
                    String(t.id) === String(tenantId)
                );
                return tenant?.tenant_name || tenant?.name || null;
            }
        } catch (error) {
            console.error('Error fetching tenant name:', error);
        }
        return null;
    };

    // Helper function to fetch site name
    const fetchSiteName = async (siteId, tenantId) => {
        if (!siteId) return null;
        try {
            const url = tenantId
                ? `${API_BASE_URL}/sites?tenant_id=${tenantId}`
                : `${API_BASE_URL}/sites`;
            const response = await fetchWithAuth(url);
            if (response.ok) {
                const data = await response.json();
                const site = data.find(s =>
                    String(s.site_id) === String(siteId) ||
                    String(s.id) === String(siteId)
                );
                return site?.name || site?.site_name || null;
            }
        } catch (error) {
            console.error('Error fetching site name:', error);
        }
        return null;
    };

    // Fetch tenant and site names when we have tenant_site_id
    // Note: Removed /api/tenant-sites/:id call as this endpoint doesn't exist
    // Instead, we'll extract tenant_id from the quotation and use it to fetch names
    useEffect(() => {
        const fetchTenantSiteNames = async () => {
            if (!quotationTenantSite?.tenant_site_id) return;
            
            // The tenant_site_id is a UUID - we need to look up the tenant and site
            // Use the existing tenant data from the quotation or fetch via sites endpoint
            try {
                // Get tenant_id from the tenant_sites table through the sites endpoint
                // Since we don't have a direct tenant-sites endpoint, we'll use the currentTenant
                // or try to infer from available data
                if (currentTenant?.id) {
                    // Use current tenant info
                    setQuotationTenantSite(prev => ({
                        ...prev,
                        tenant_id: currentTenant.id,
                        tenant_name: currentTenant.name
                    }));
                }
            } catch (error) {
                console.error('Error setting tenant_site details:', error);
            }
        };

        fetchTenantSiteNames();
    }, [quotationTenantSite?.tenant_site_id, currentTenant?.id]);


    // Keep quote-side plan hours aligned with the live service-plan totals from the shell
    useEffect(() => {
        if (hourTotalsAreEmpty(planHourTotals)) return;
        setTotalsCarePlan(planHourTotals);
    }, [planHourTotals]);

    // Reset cached care-plan totals when the quotation identity changes
    useEffect(() => {
        carePlanTotalsRevisionRef.current = -1;
        activityMetaCacheRef.current = {};
        // Keep shell plan hours visible immediately on a newly opened/created quote
        if (!hourTotalsAreEmpty(planHourTotals)) {
            setTotalsCarePlan(planHourTotals);
        }
    }, [Qid]);

    //
    useEffect(() => {
        if (!Qid) return;

        // Already have totals for this plan revision — nothing to do (makes tab switches instant)
        if (carePlanTotalsRevisionRef.current === planRevision) {
            setIsRefreshingCarePlanTotals(false);
            return;
        }

        // While the quote panel is hidden, only prefetch after a plan save (revision bump).
        // Skip the initial hidden mount so we don't fetch before the user opens Quote.
        if (!isActive && carePlanTotalsRevisionRef.current === -1 && planRevision === 0) return;

        const normalizeActivityId = (rawId) => {
            if (rawId == null) return null;
            const s = String(rawId);
            if (s.startsWith('legacy_activity_')) return s.slice('legacy_activity_'.length);
            if (s.startsWith('activity_')) return s.slice('activity_'.length);
            return s;
        };

        const getMultiplierValue = (multiplier) => {
            if (multiplier === null || multiplier === undefined || multiplier === '') return 1;
            const parsed = parseFloat(multiplier);
            return isNaN(parsed) ? 1 : parsed;
        };

        let cancelled = false;
        const targetRevision = planRevision;
        // Only show overlay when refreshing already-loaded totals (plan change), not cold first fetch
        const isRefresh = carePlanTotalsRevisionRef.current !== -1;
        if (isRefresh) {
            setIsRefreshingCarePlanTotals(true);
        }

        const fetchCarePlan = async () => {
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/careplansQuote/${Qid}`);

                if (cancelled) return;

                if (response.ok) {
                    const data = await response.json();

                    const fetchedCarePlanId = data.care_plan_id || data.carePlanId || data.CarePlanId || data.id;
                    if (fetchedCarePlanId) {
                        setCarePlanID(fetchedCarePlanId);
                    }

                    const carePlanLineItems = data.lineItems || data.line_items || [];

                    // Reuse cached activity metadata on refresh; only hit the API when the cache is empty
                    let activityMetaMap = activityMetaCacheRef.current;
                    const cacheEmpty = !activityMetaMap || Object.keys(activityMetaMap).length === 0;
                    if (cacheEmpty) {
                        activityMetaMap = {};
                        try {
                            const activitiesResp = await fetchWithAuth(`${API_BASE_URL}/careitemactivities?customer_id=${id}`);
                            if (activitiesResp.ok) {
                                const activitiesData = await activitiesResp.json();
                                if (Array.isArray(activitiesData)) {
                                    activitiesData.forEach(a => {
                                        const norm = normalizeActivityId(a.activity_id ?? a.ActivityID);
                                        if (norm) activityMetaMap[norm] = a;
                                    });
                                }
                            }
                        } catch (e) {
                            console.error('Failed to fetch activities for hours cross-reference:', e);
                        }
                        activityMetaCacheRef.current = activityMetaMap;
                    }

                    if (cancelled) return;

                    const normalizedCarePlanItems = carePlanLineItems.map(item => {
                        const meta = item.metadata || {};
                        const activityMeta = meta.activity || {};
                        const norm = normalizeActivityId(item.activity_id ?? item.activity_key);
                        const activityRecord = norm ? activityMetaMap[norm] : null;
                        const activityRecordMeta = activityRecord?.metadata || {};

                        return {
                            ...item,
                            item_name: item.activity_name || item.activityName || activityMeta?.name || meta.activity_name || item.item_name || 'Unknown Activity',
                            sn_hours_day: parseFloat(item.sn_hours_day || meta.sn_hours_day || activityRecordMeta.sn_hours_day || activityRecord?.sn_hours_day || meta.catalogue?.defaults?.sn_hours_day || 0),
                            sn_hours_night: parseFloat(item.sn_hours_night || meta.sn_hours_night || activityRecordMeta.sn_hours_night || activityRecord?.sn_hours_night || meta.catalogue?.defaults?.sn_hours_night || 0),
                            ena_hours_day: parseFloat(item.ena_hours_day || meta.ena_hours_day || activityRecordMeta.ena_hours_day || activityRecord?.ena_hours_day || meta.catalogue?.defaults?.ena_hours_day || 0),
                            ena_hours_night: parseFloat(item.ena_hours_night || meta.ena_hours_night || activityRecordMeta.ena_hours_night || activityRecord?.ena_hours_night || meta.catalogue?.defaults?.ena_hours_night || 0),
                            cw_hours_day: parseFloat(item.cw_hours_day || meta.cw_hours_day || activityRecordMeta.cw_hours_day || activityRecord?.cw_hours_day || meta.catalogue?.defaults?.cw_hours_day || 0),
                            cw_hours_night: parseFloat(item.cw_hours_night || meta.cw_hours_night || activityRecordMeta.cw_hours_night || activityRecord?.cw_hours_night || meta.catalogue?.defaults?.cw_hours_night || 0),
                            benefit_body: parseFloat(item.benefit_body || meta.benefit_body || activityRecordMeta.benefit_body || activityRecord?.benefit_body || meta.catalogue?.defaults?.benefit_body || 0),
                            benefit_mind: parseFloat(item.benefit_mind || meta.benefit_mind || activityRecordMeta.benefit_mind || activityRecord?.benefit_mind || meta.catalogue?.defaults?.benefit_mind || 0),
                            benefit_social: parseFloat(item.benefit_social || meta.benefit_social || activityRecordMeta.benefit_social || activityRecord?.benefit_social || meta.catalogue?.defaults?.benefit_social || 0),
                            frequency: item.frequency || meta.frequency || activityRecord?.frequency || meta.catalogue?.defaults?.frequency || 1,
                        };
                    });

                    const calculatedTotalsCarePlan = normalizedCarePlanItems.reduce((totalsAcc, item) => {
                        const frequency = parseFloat(item.frequency || item.Frequency || 1) || 1;
                        const multiplier = getMultiplierValue(item.multiplier);
                        const snDayMinutes = parseFloat(item.sn_hours_day || 0);
                        const snNightMinutes = parseFloat(item.sn_hours_night || 0);
                        const enaDayMinutes = parseFloat(item.ena_hours_day || 0);
                        const enaNightMinutes = parseFloat(item.ena_hours_night || 0);
                        const cwDayMinutes = parseFloat(item.cw_hours_day || 0);
                        const cwNightMinutes = parseFloat(item.cw_hours_night || 0);

                        return {
                            sn_hours_day: totalsAcc.sn_hours_day + ((frequency * snDayMinutes * multiplier) / 60),
                            sn_hours_night: totalsAcc.sn_hours_night + ((frequency * snNightMinutes * multiplier) / 60),
                            ena_hours_day: totalsAcc.ena_hours_day + ((frequency * enaDayMinutes * multiplier) / 60),
                            ena_hours_night: totalsAcc.ena_hours_night + ((frequency * enaNightMinutes * multiplier) / 60),
                            cw_hours_day: totalsAcc.cw_hours_day + ((frequency * cwDayMinutes * multiplier) / 60),
                            cw_hours_night: totalsAcc.cw_hours_night + ((frequency * cwNightMinutes * multiplier) / 60),
                        };
                    }, {
                        sn_hours_day: 0,
                        sn_hours_night: 0,
                        ena_hours_day: 0,
                        ena_hours_night: 0,
                        cw_hours_day: 0,
                        cw_hours_night: 0,
                    });

                    setTotalsCarePlan(
                        !hourTotalsAreEmpty(planHourTotals)
                            ? planHourTotals
                            : calculatedTotalsCarePlan
                    );

                    const calculatedBenefitTotals = normalizedCarePlanItems.reduce((benefitsTotalsAcc, item) => {
                        const totalMinutes = Number(item.sn_hours_day || 0) +
                            Number(item.sn_hours_night || 0) +
                            Number(item.ena_hours_day || 0) +
                            Number(item.ena_hours_night || 0) +
                            Number(item.cw_hours_day || 0) +
                            Number(item.cw_hours_night || 0);

                        return {
                            benefit_body:
                                (parseFloat(benefitsTotalsAcc.benefit_body || 0) +
                                    totalMinutes * ((Number(item.benefit_body) / 100) || 0)).toFixed(2),
                            benefit_mind:
                                (parseFloat(benefitsTotalsAcc.benefit_mind || 0) +
                                    totalMinutes * ((Number(item.benefit_mind) / 100) || 0)).toFixed(2),
                            benefit_social:
                                (parseFloat(benefitsTotalsAcc.benefit_social || 0) +
                                    totalMinutes * ((Number(item.benefit_social) / 100) || 0)).toFixed(2),
                        };
                    }, {
                        benefit_body: 0,
                        benefit_mind: 0,
                        benefit_social: 0,
                    });

                    setbenefitsTotals(calculatedBenefitTotals);
                } else if (response.status !== 404) {
                    console.error('Failed to fetch service plan');
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('An error occurred while fetching the service plan:', error);
                }
            } finally {
                // Mark revision applied on settle so tab re-open does not keep treating as missing.
                // Shell planHourTotals cover the gap if the fetch is empty/failed right after create.
                if (!cancelled) {
                    carePlanTotalsRevisionRef.current = targetRevision;
                    setIsRefreshingCarePlanTotals(false);
                }
            }
        };

        fetchCarePlan();

        return () => {
            cancelled = true;
            setIsRefreshingCarePlanTotals(false);
        };
    }, [id, Qid, isActive, planRevision, planHourTotals]);
    //
    const handleItemChange = (e) => {
        const selected = Array.from(e.target.selectedOptions)
            .map(opt => {
                try {
                    const item = JSON.parse(opt.value);
                    const meta = item.metadata || {};
                    return {
                        ...item,
                        sn_hours_day: item.sn_hours_day ?? meta.sn_hours_day ?? 0,
                        sn_hours_night: item.sn_hours_night ?? meta.sn_hours_night ?? 0,
                        ena_hours_day: item.ena_hours_day ?? meta.ena_hours_day ?? 0,
                        ena_hours_night: item.ena_hours_night ?? meta.ena_hours_night ?? 0,
                        cw_hours_day: item.cw_hours_day ?? meta.cw_hours_day ?? 0,
                        cw_hours_night: item.cw_hours_night ?? meta.cw_hours_night ?? 0,
                    };
                } catch { return null; }
            })
            .filter(Boolean);
        setSelectedItems(selected);
    };



    // Helper function to get multiplier value consistently
    const getMultiplier = (multiplier) => {
        if (multiplier === null || multiplier === undefined || multiplier === '') {
            return 1;
        }
        const parsed = parseFloat(multiplier);
        return isNaN(parsed) ? 1 : parsed;
    };

    const isDefaultQuoteItem = (item) => {
        if (isCatalogueDetachedLine(item)) return false;
        return defaultItems.some(def =>
            String(def) === String(item.price_list_item_id) ||
            String(def) === String(item.tenant_site_price_item_id) ||
            String(def) === String(item.price_list_item_key)
        );
    };

    const handleMultiplierChange = async (index, value) => {
        if (!isAdmin && isDefaultQuoteItem(lineItems[index])) return;
        // Allow empty values while typing
        let newMultiplier;
        if (value === '') {
            newMultiplier = '';
        } else {
            const parsed = parseFloat(value);
            newMultiplier = isNaN(parsed) ? 1 : parsed; // Only default to 1 if parsing fails
        }

        const updatedLineItems = [...lineItems];

        // Just update the multiplier - the original values are already stored in the line item
        updatedLineItems[index] = {
            ...updatedLineItems[index],
            multiplier: newMultiplier,
        };

        setLineItems(updatedLineItems);
        const newTotals = calculateTotals(updatedLineItems, hourlyRates);
        setTotals(newTotals);

        // Sync with backend using the new API
        if (Quotation?.quotation_id && updatedLineItems[index]?.id && newMultiplier !== '') {
            try {
                const response = await fetchWithAuth(`${API_BASE_URL}/quotations/${Quotation.quotation_id}/line-items/${updatedLineItems[index].id}`, {
                    method: 'PUT',
                    body: JSON.stringify({ multiplier: newMultiplier })
                });

                if (!response.ok) {
                    const message = await readApiErrorMessage(
                        response,
                        'We couldn’t update the multiplier. Please try again.'
                    );
                    console.error('Failed to sync multiplier to backend:', message);
                    showToast(message, 'danger');
                }
            } catch (error) {
                console.error('Error syncing multiplier to backend:', error);
            }
        }
    };

    // Add this function to handle when the input loses focus
    const handleMultiplierBlur = (index, value) => {
        if (!isAdmin && isDefaultQuoteItem(lineItems[index])) return;
        const updatedLineItems = [...lineItems];

        // If the input is empty or invalid, set the multiplier back to 1
        if (value === '' || isNaN(parseFloat(value))) {
            updatedLineItems[index] = {
                ...updatedLineItems[index],
                multiplier: 1,
            };
        }

        setLineItems(updatedLineItems);
        const newTotals = calculateTotals(updatedLineItems, hourlyRates);
        setTotals(newTotals);
    };

    const handleRemoveLineItem = async (index) => {
        const removedItem = lineItems[index];
        if (!removedItem) return;
        if (!isAdmin && isDefaultQuoteItem(removedItem)) return;
        
        // Try to delete from backend using the new API if it has an id
        // The id should be the quote_line_id from the database
        // Only attempt backend delete if we have valid IDs
        if (Quotation?.quotation_id && Quotation.quotation_id !== 'undefined' && removedItem?.id) {
            try {
                const itemId = removedItem.id;
                
                const response = await fetchWithAuth(`${API_BASE_URL}/quotations/${Quotation.quotation_id}/line-items/${itemId}`, {
                    method: 'DELETE',
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Failed to delete line item from backend:', response.status, errorText);
                    // Continue with local deletion anyway
                } else {
                    console.log('Line item deleted from backend successfully');
                }
            } catch (error) {
                console.error('Error deleting line item from backend:', error);
                // Continue with local deletion anyway
            }
        } else {
            console.warn('Cannot delete from backend - missing quotation_id or item id:', {
                quotation_id: Quotation?.quotation_id,
                item_id: removedItem?.id
            });
        }

        const updatedLineItems = lineItems.filter((_, i) => i !== index);

        // Calculate values to subtract based on the multiplier
        const multiplier = getMultiplier(removedItem.multiplier);
        const hoursToSubtract = {
            price: (parseFloat(parseCurrency(removedItem.price)) * multiplier),
            sn_hours_day: (parseFloat(removedItem.sn_hours_day || 0) * multiplier),
            sn_hours_night: (parseFloat(removedItem.sn_hours_night || 0) * multiplier),
            ena_hours_day: (parseFloat(removedItem.ena_hours_day || 0) * multiplier),
            ena_hours_night: (parseFloat(removedItem.ena_hours_night || 0) * multiplier),
            cw_hours_day: (parseFloat(removedItem.cw_hours_day || 0) * multiplier),
            cw_hours_night: (parseFloat(removedItem.cw_hours_night || 0) * multiplier),
            catering: (parseFloat(parseCurrency(removedItem.catering) || 0) * multiplier),
            cleaning: (parseFloat(parseCurrency(removedItem.cleaning) || 0) * multiplier),
            laundry: (parseFloat(parseCurrency(removedItem.laundry) || 0) * multiplier),
            other: (parseFloat(parseCurrency(removedItem.other) || 0) * multiplier),
        };

        const newTotals = {
            price: totals.price - hoursToSubtract.price,
            sn_hours_day: totals.sn_hours_day - hoursToSubtract.sn_hours_day,
            sn_hours_night: totals.sn_hours_night - hoursToSubtract.sn_hours_night,
            ena_hours_day: totals.ena_hours_day - hoursToSubtract.ena_hours_day,
            ena_hours_night: totals.ena_hours_night - hoursToSubtract.ena_hours_night,
            cw_hours_day: totals.cw_hours_day - hoursToSubtract.cw_hours_day,
            cw_hours_night: totals.cw_hours_night - hoursToSubtract.cw_hours_night,
            catering: totals.catering - hoursToSubtract.catering,
            cleaning: totals.cleaning - hoursToSubtract.cleaning,
            laundry: totals.laundry - hoursToSubtract.laundry,
            other: totals.other - hoursToSubtract.other,
        };
        newTotals.Labour =
            (((newTotals.sn_hours_day + newTotals.sn_hours_night) / 60) * hourlyRates.SN) +
            (((newTotals.ena_hours_day + newTotals.ena_hours_night) / 60) * hourlyRates.ENA) +
            (((newTotals.cw_hours_day + newTotals.cw_hours_night) / 60) * hourlyRates.CW);

        setTotals(newTotals);
        setLineItems(updatedLineItems);
    };

    // Identity tokens for a price-list catalogue row or quotation line item.
    // Same display name with a different key/id must NOT be treated as the same item.
    const getPriceItemIdentityKeys = (item) => {
        const keys = new Set();
        const add = (value) => {
            if (value === null || value === undefined || value === '') return;
            const s = String(value);
            if (s === 'undefined' || s === 'null') return;
            keys.add(s);
        };
        add(item.price_list_item_key);
        add(item.price_list_item_id);
        add(item.tenant_site_price_item_id);
        // Catalogue API exposes the tenant-site row as `id` (not tenant_site_price_item_id).
        // Line items overwrite `id` with quote_line_id — skip those.
        if (item.quote_line_id == null && item.price_list_item_key && item.id != null) {
            add(item.id);
        }
        return keys;
    };

    const isPriceItemAlreadyOnQuotation = (item, existingItems = lineItems) => {
        const itemKeys = getPriceItemIdentityKeys(item);
        if (itemKeys.size === 0) return false;
        return existingItems.some((lineItem) => {
            const lineKeys = getPriceItemIdentityKeys(lineItem);
            for (const key of itemKeys) {
                if (lineKeys.has(key)) return true;
            }
            return false;
        });
    };

    const handleAddLineItem = async () => {
        if (selectedItems.length === 0) {
            showToast('Please select at least one item.', 'warning');
            return;
        }
        setIsAddingItems(true);

        let newLineItems = [...lineItems];

        for (const selectedItem of selectedItems) {
            if (!selectedItem.price_list_item_id) continue;

            // Skip if already in quotation (by ID/key only — same name with a different ID is allowed)
            if (isPriceItemAlreadyOnQuotation(selectedItem, newLineItems)) continue;


            const selectedMultiplier = getMultiplier(selectedItem.multiplier);
            const priceListItemId = selectedItem.price_list_item_id;
            const isStringKey = isNaN(parseInt(priceListItemId));

            const newItem = {
                ...selectedItem,
                multiplier: selectedMultiplier,
                price: parseFloat(parseCurrency(selectedItem.price) || 0),
                sn_hours_day: parseFloat(selectedItem.sn_hours_day || 0),
                sn_hours_night: parseFloat(selectedItem.sn_hours_night || 0),
                ena_hours_day: parseFloat(selectedItem.ena_hours_day || 0),
                ena_hours_night: parseFloat(selectedItem.ena_hours_night || 0),
                cw_hours_day: parseFloat(selectedItem.cw_hours_day || 0),
                cw_hours_night: parseFloat(selectedItem.cw_hours_night || 0),
                catering: parseFloat(parseCurrency(selectedItem.catering) || 0),
                cleaning: parseFloat(parseCurrency(selectedItem.cleaning) || 0),
                laundry: parseFloat(parseCurrency(selectedItem.laundry) || 0),
                other: parseFloat(parseCurrency(selectedItem.other) || 0),
                ...(isStringKey && { price_list_item_key: priceListItemId }),
            };

            if (Quotation?.quotation_id && Quotation.quotation_id !== 'undefined') {
                try {
                    const payload = {
                        ...(!isStringKey && { tenant_site_price_item_id: parseInt(priceListItemId, 10) }),
                        ...(isStringKey && { price_list_item_key: priceListItemId }),
                        ...(isStringKey && { tenant_site_id: quotationTenantSite?.tenant_site_id }),
                        multiplier: selectedMultiplier,
                        sn_hours_day: Number(selectedItem.sn_hours_day) || 0,
                        sn_hours_night: Number(selectedItem.sn_hours_night) || 0,
                        ena_hours_day: Number(selectedItem.ena_hours_day) || 0,
                        ena_hours_night: Number(selectedItem.ena_hours_night) || 0,
                        cw_hours_day: Number(selectedItem.cw_hours_day) || 0,
                        cw_hours_night: Number(selectedItem.cw_hours_night) || 0,
                        catering: parseFloat(parseCurrency(selectedItem.catering) || 0),
                        cleaning: parseFloat(parseCurrency(selectedItem.cleaning) || 0),
                        laundry: parseFloat(parseCurrency(selectedItem.laundry) || 0),
                        other: parseFloat(parseCurrency(selectedItem.other) || 0),
                        frequency: selectedItem.frequency || 'Daily',
                    };

                    const response = await fetchWithAuth(`${API_BASE_URL}/quotations/${Quotation.quotation_id}/line-items`, {
                        method: 'POST',
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        const result = await response.json();
                        newItem.id = result.quote_line_id || result.id;
                        newItem.unit_price = result.unit_price;
                        newItem.line_total = result.line_total;
                    } else {
                        const errorDetail = getApiErrorMessage(
                            await response.text(),
                            'This item could not be saved to the quotation.'
                        );
                        console.error('Failed to add line item to backend:', response.status, errorDetail);
                        setIsAddingItems(false);
                        showConfirm(
                            <>
                                <p>{errorDetail}</p>
                                <p className="mb-0">If this keeps happening, try creating a new quotation from a service plan that has a site linked, or contact an administrator.</p>
                            </>,
                            () => {},
                            { title: 'Couldn’t add item', confirmLabel: 'OK', confirmVariant: 'primary' }
                        );
                        return;
                    }
                } catch (error) {
                    console.error('Error adding line item to backend:', error);
                    setIsAddingItems(false);
                    showConfirm(
                        <p className="mb-0">We couldn’t save this item. Please check your connection and try again. If it keeps failing, create a new quotation from a service plan with a site linked.</p>,
                        () => {},
                        { title: 'Couldn’t add item', confirmLabel: 'OK', confirmVariant: 'primary' }
                    );
                    return;
                }
            }

            newLineItems.push(newItem);
        }

        const addedCount = newLineItems.length - lineItems.length;
        const updatedLineItems = sortItemsWithDefaultsFirst(newLineItems);
        setLineItems(updatedLineItems);
        setHasAddedItem(true);
        const newTotals = calculateTotals(updatedLineItems, hourlyRates);
        setTotals(newTotals);
        setSelectedItems([]);
        setIsAddingItems(false);
        if (addedCount > 0) {
            showToast(`${addedCount} item${addedCount === 1 ? '' : 's'} added to quotation`);
        } else {
            showToast('No new items added — all selected are already in this quotation', 'warning');
        }
    };

    const handleBulkRemoveLineItems = () => {
        if (bulkRemoveLineItemIds.size === 0) return;
        const selectedItemsForRemove = lineItems.filter(item => bulkRemoveLineItemIds.has(item.id));
        if (!isAdmin && selectedItemsForRemove.some(item => isDefaultQuoteItem(item))) {
            showToast(ADMIN_ONLY_BULK_REMOVE_DEFAULTS_TIP, 'warning');
            return;
        }
        const n = bulkRemoveLineItemIds.size;
        showConfirm(
            `Remove ${n} selected item(s)?`,
            async () => {
                for (const itemId of bulkRemoveLineItemIds) {
                    if (Quotation?.quotation_id && Quotation.quotation_id !== 'undefined' && itemId) {
                        try {
                            await fetchWithAuth(`${API_BASE_URL}/quotations/${Quotation.quotation_id}/line-items/${itemId}`, {
                                method: 'DELETE',
                            });
                        } catch (error) {
                            console.error('Error deleting line item from backend:', error);
                        }
                    }
                }
                const updatedLineItems = lineItems.filter(item => !bulkRemoveLineItemIds.has(item.id));
                setLineItems(updatedLineItems);
                const newTotals = calculateTotals(updatedLineItems, hourlyRates);
                setTotals(newTotals);
                setBulkRemoveLineItemIds(new Set());
            },
            { title: 'Remove Items', confirmLabel: 'Remove' }
        );
    };


    const handleSubmit = () => {
        showConfirm(
            'Are you sure you want to save a new quotation?',
            async () => {
                try {
                    const tenantSiteId = quotationTenantSite?.tenant_site_id;
                    const payload = {
                        customer_id: id,
                        customer_care_type_id: customerCareTypeId,
                        ...(carePlanID && { care_plan_id: carePlanID }),
                        ...(tenantSiteId && { tenant_site_id: tenantSiteId }),
                    };

                    const response = await fetchWithAuth(`${API_BASE_URL}/quotations`, {
                        method: 'POST',
                        body: JSON.stringify(payload)
                    });

                    if (response.ok) {
                        const result = await response.json();
                        const newQuotationId = result.quotation_id;
                        showToast(`Quotation created successfully!${result.default_items_added ? ` ${result.default_items_added} default item(s) added.` : ''}`);
                        if (newQuotationId) {
                            if (carePlanID) {
                                navigate(`/careplans/${id}/${carePlanID}/quote/${newQuotationId}`);
                            } else {
                                navigate(`/quotation/${id}/${new Date().toISOString().split('T')[0]}/${newQuotationId}`);
                            }
                        }
                        setIsSaved(true);
                        setEdit(false);
                        setBulkRemoveLineItemIds(new Set());
                    } else {
                        const message = await readApiErrorMessage(
                            response,
                            'We couldn’t create the quotation. Please try again.'
                        );
                        console.error('Failed to create quotation:', message);
                        showToast(message, 'danger');
                    }
                } catch (error) {
                    console.error('An error occurred:', error);
                    showToast('We couldn’t create the quotation. Please check your connection and try again.', 'danger');
                }
            },
            { title: 'Save New Quotation', confirmLabel: 'Save', confirmVariant: 'success' }
        );
    };

    const handleUpdateQuotation = async () => {
        // Validate Quotation exists and has required fields
        if (!Quotation || !Quotation.quotation_id) {
            showToast('Quotation data is not loaded yet. Please wait and try again.', 'warning');
            return;
        }

        try {
            // Use the new API endpoint to update quotation header (status, notes, etc.)
            const payload = {
                status: Quotation.status || 0,
                notes: notes,
                customer_discount: CustomerDiscount
            };

            const response = await fetchWithAuth(`${API_BASE_URL}/quotations/${Quotation.quotation_id}`, {
                method: 'PUT',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                setTemp(lineItems);
                setIsSaved(true);
                setEdit(false);
                setBulkRemoveLineItemIds(new Set());
                const result = await response.json();
                showToast('Quotation updated successfully!');

                // Refresh the quotation data to reflect updates
                try {
                    const refreshResponse = await fetchWithAuth(`${API_BASE_URL}/quotations/${Quotation.quotation_id}`);
                    if (refreshResponse.ok) {
                        const data = await refreshResponse.json();
                        // Handle both PascalCase (Quotation) and snake_case (quotation) from API
                        const quotationData = data.quotation || data.Quotation;
                        setQuotation(quotationData);
                        // Update CarePlanID if available in the quotation data
                        const refreshedCarePlanId = quotationData?.care_plan_id || quotationData?.CarePlanId || quotationData?.carePlanId;
                        if (refreshedCarePlanId) {
                            setCarePlanID(refreshedCarePlanId);
                        }
                        // Handle both camelCase (lineItems) and snake_case (line_items) from API
                        const lineItemsData = data.line_items || data.lineItems;
                        
                        // Fetch price list items to use as fallback for hour fields (like initial load does)
                        let priceListItemsMap = {};
                        try {
                            const tenantSiteIdFromQuote = quotationData?.tenant_site_id;
                            const tenantIdParam = tenantSiteIdFromQuote
                                ? `&tenant_site_id=${tenantSiteIdFromQuote}`
                                : (currentTenant?.id ? `&tenant_id=${currentTenant.id}` : '');
                            const priceListResponse = await fetchWithAuth(`${API_BASE_URL}/pricelistItems?customer_id=${id}${tenantIdParam}`);
                            if (priceListResponse.ok) {
                                const priceListData = await priceListResponse.json();
                                if (Array.isArray(priceListData)) {
                                    priceListData.forEach(pli => {
                                        if (pli.price_list_item_id) {
                                            priceListItemsMap[pli.price_list_item_id] = pli;
                                        }
                                        if (pli.price_list_item_key) {
                                            priceListItemsMap[pli.price_list_item_key] = pli;
                                        }
                                    });
                                }
                            }
                        } catch (e) {
                            console.error('Failed to fetch price list items for refresh mapping:', e);
                        }
                        
                        // Map the new API response to our format (add price_list_item_id from denormalized fields)
                        // Use the same priority logic as the initial fetchQuotation
                        const mappedLineItems = lineItemsData.map(item =>
                            overlayQuoteLineFromCatalogue(item, lookupCatalogueItem(item, priceListItemsMap))
                        );
                        
                        // Sort line items: defaults first, then alphabetically
                        const sortedLineItems = sortItemsWithDefaultsFirst(mappedLineItems);
                        setLineItems(sortedLineItems);
                        setTemp(sortedLineItems);
                        const calculatedTotals = calculateTotals(mappedLineItems, hourlyRates);
                        setTotals(calculatedTotals);
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh quotation after update:', refreshError);
                }
            } else {
                const message = await readApiErrorMessage(
                    response,
                    'We couldn’t update the quotation. Please try again.'
                );
                console.error('Failed to update quotation:', message);
                showToast(message, 'danger');
            }
        } catch (error) {
            console.error('An error occurred while updating the quotation:', error);
            showToast('We couldn’t update the quotation. Please check your connection and try again.', 'danger');
        }
    };

    const pieDataBenefits = {
        labels: ['Mind', 'Body', 'Social'],
        datasets: [
            {
                label: 'Services Breakdown',
                data: [
                    benefitsTotals.benefit_body,
                    benefitsTotals.benefit_mind,
                    benefitsTotals.benefit_social,
                ],
                backgroundColor: [
                    'rgba(255, 99, 132, 0.6)',
                    'rgba(54, 162, 235, 0.6)',
                    'rgba(255, 206, 86, 0.6)',
                ],
                borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',

                ],
                borderWidth: 1,
            },
        ],
    };



    const pieDataTotals = {
        labels: ['catering', 'cleaning', 'Labour', 'laundry', 'other'],
        datasets: [
            {
                label: 'Services Breakdown',
                data: [
                    totals.catering,
                    totals.cleaning,
                    totals.Labour,
                    totals.laundry,
                    totals.other
                ],
                backgroundColor: [
                    'rgba(255, 99, 132, 0.6)',
                    'rgba(54, 162, 235, 0.6)',
                    'rgba(255, 206, 86, 0.6)',
                    'rgba(75, 192, 192, 0.6)',
                    'rgba(153, 102, 255, 0.6)'
                ],
                borderColor: [
                    'rgba(255, 99, 132, 1)',
                    'rgba(54, 162, 235, 1)',
                    'rgba(255, 206, 86, 1)',
                    'rgba(75, 192, 192, 1)',
                    'rgba(153, 102, 255, 1)'
                ],
                borderWidth: 1,
            },
        ],
    };
    const snTotalHours = totals.sn_hours_day + totals.sn_hours_night;

    const pieDataSN = {
        labels: [`${getAbbreviation('SN')} Hours Day`, `${getAbbreviation('SN')} Hours Night`],
        datasets: [
            {
                label: `Total ${getAbbreviation('SN')} Hours`,
                data: [displayPlanHours.sn_hours_day, displayPlanHours.sn_hours_night],
                backgroundColor: ['#36A2EB', '#FF6384'],
                hoverBackgroundColor: ['#36A2EB', '#FF6384'],
                borderWidth: 1,
            },
        ],
    };

    const ENATotalHours = totals.sn_hours_day + totals.sn_hours_night;

    const pieDataENA = {
        labels: [`${getAbbreviation('ENA')} Hours Day`, `${getAbbreviation('ENA')} Hours Night`],
        datasets: [
            {
                label: `Total ${getAbbreviation('ENA')} Hours`,
                data: [displayPlanHours.ena_hours_day, displayPlanHours.ena_hours_night],
                backgroundColor: ['#36A2EB', '#FF6384'],
                hoverBackgroundColor: ['#36A2EB', '#FF6384'],
            },
        ],
    };
    const CWTotalHours = totals.cw_hours_day + totals.cw_hours_night;
    const pieDataCW = {
        labels: [`${getAbbreviation('CW')} Hours Day`, `${getAbbreviation('CW')} Hours Night`],
        datasets: [
            {
                label: `Total ${getAbbreviation('CW')} Hours`,
                data: [displayPlanHours.cw_hours_day, displayPlanHours.cw_hours_night],
                backgroundColor: ['#36A2EB', '#FF6384'],
                hoverBackgroundColor: ['#36A2EB', '#FF6384'],
            },
        ],
    };


    const total = lineItems.reduce((sum, item) => {
        return sum + (parseCurrency(item.price) * getMultiplier(item.multiplier));
    }, 0);

    const [notes, setNotes] = useState('');
    const [wordCount, setWordCount] = useState(0);
    const NOTES_WORD_LIMIT = 850;

    // Helper function to count words from HTML content
    const countWordsFromHtml = (html) => {
        if (!html) return 0;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const text = tempDiv.textContent || tempDiv.innerText || '';
        return text.trim().split(/\s+/).filter(word => word.length > 0).length;
    };

    // ReactQuill toolbar configuration without hyperlink option
    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['clean']
        ]
    };

    useEffect(() => {
        if (Quotation) {
            setNotes(Quotation.notes || '');
            setWordCount(countWordsFromHtml(Quotation.notes || ''));
        }
    }, [Quotation]);

    useEffect(() => {
        const hasChanges =
            JSON.stringify(lineItems) !== JSON.stringify(temp) ||
            CustomerDiscount !== Quotation?.customer_discount ||
            notes !== Quotation?.notes;

        setIsSaved(!hasChanges);
    }, [lineItems, CustomerDiscount, Quotation, notes]);

    const handleNotesChange = async (newNotes) => {
        const newWordCount = countWordsFromHtml(newNotes);
        
        // Prevent input if exceeding word limit
        if (newWordCount > NOTES_WORD_LIMIT) {
            return;
        }
        
        setNotes(newNotes);
        setWordCount(newWordCount);

        try {
            const response = await fetchWithAuth(`${API_BASE_URL}/quotes/updateNotes`, {
                method: 'PUT',
                body: JSON.stringify({
                    quoteId: Qid,
                    notes: newNotes
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to update notes');
            }
        } catch (error) {
            console.error('Error updating notes:', error);
        }
    };

    const handleActivateQuotation = () => {
        if (!Quotation || !Quotation.quotation_id) {
            showToast('Quotation ID is missing', 'danger');
            return;
        }

        showConfirm(
            'Are you sure you want to activate this quotation?',
            async () => {
                setIsActivating(true);
                try {
                    const response = await fetchWithAuth(`${API_BASE_URL}/quotes/${Quotation.quotation_id}/set-active`, {
                        method: 'PUT',
                    });

                    if (response.ok) {
                        showToast('Quotation activated successfully!');
                        try {
                            const refreshResponse = await fetchWithAuth(`${API_BASE_URL}/quotations/${Qid}`);
                            if (refreshResponse.ok) {
                                const data = await refreshResponse.json();
                                const quotationData = data.Quotation || data.quotation;
                                setQuotation(quotationData);
                            }
                        } catch (refreshError) {
                            console.error('Failed to refresh quotation after activation:', refreshError);
                        }
                    } else {
                        const message = await readApiErrorMessage(
                            response,
                            'We couldn’t activate this quotation. Please try again.'
                        );
                        showToast(message, 'danger');
                    }
                } catch (error) {
                    console.error('Error activating quotation:', error);
                    showToast('We couldn’t activate this quotation. Please check your connection and try again.', 'danger');
                } finally {
                    setIsActivating(false);
                }
            },
            { title: 'Activate Quotation', confirmLabel: 'Activate', confirmVariant: 'success' }
        );
    };

    // Functions for copying from previous quotation
    const handleOpenCopyModal = async () => {
        setShowCopyModal(true);
        setLoadingQuotations(true);
        setSelectedQuotationId('');
        setSelectedQuotationLineItems([]);
        setSelectedLineItemIds([]);

        try {
            // Use the new API to get all quotations for this customer
            const response = await fetchWithAuth(`${API_BASE_URL}/quotations?customer_id=${id}`);

            if (response.ok) {
                const data = await response.json();
                // Handle both array response and object with quotations property
                const quotationsData = Array.isArray(data) ? data : (data.quotations || data.Quotations || []);
                // Filter out the current quotation
                const otherQuotations = quotationsData.filter(
                    q => q.quotation_id !== Quotation?.quotation_id
                );
                setAvailableQuotations(otherQuotations);
            } else {
                console.error('Failed to fetch quotations');
                setAvailableQuotations([]);
            }
        } catch (error) {
            console.error('Error fetching quotations:', error);
            setAvailableQuotations([]);
        } finally {
            setLoadingQuotations(false);
        }
    };

    const handleQuotationSelect = async (quotationId, dateCreated) => {
        if (!quotationId || !dateCreated) {
            setSelectedQuotationLineItems([]);
            setSelectedLineItemIds([]);
            return;
        }

        setLoadingLineItems(true);
        setSelectedLineItemIds([]);

        try {
            // Use the new API to get quotation with line items
            const response = await fetchWithAuth(`${API_BASE_URL}/quotations/${quotationId}`);

            if (response.ok) {
                const data = await response.json();
                // Handle both camelCase (lineItems) and snake_case (line_items) from API
                // The new API returns: { quotation: {...}, line_items: [...] }
                const lineItemsData = data.line_items || data.lineItems || data.line_items;
                if (lineItemsData && Array.isArray(lineItemsData)) {
                    // Map the new API response to our format
                    // Also include hour fields for proper calculation
                    const mappedLineItems = lineItemsData.map(item =>
                        overlayQuoteLineFromCatalogue(item, null)
                    );
                    setSelectedQuotationLineItems(mappedLineItems);
                } else {
                    setSelectedQuotationLineItems([]);
                }
            } else {
                console.error('Failed to fetch line items');
                setSelectedQuotationLineItems([]);
            }
        } catch (error) {
            console.error('Error fetching line items:', error);
            setSelectedQuotationLineItems([]);
        } finally {
            setLoadingLineItems(false);
        }
    };

    const handleLineItemToggle = (lineItemId) => {
        setSelectedLineItemIds(prev => {
            if (prev.includes(lineItemId)) {
                return prev.filter(id => id !== lineItemId);
            } else {
                return [...prev, lineItemId];
            }
        });
    };

    const handleCopySelectedItems = () => {
        if (selectedLineItemIds.length === 0) {
            showToast('Please select at least one item to copy.', 'warning');
            return;
        }

        // Get current line item IDs to exclude duplicates (check both ID fields)
        const currentLineItemIds = lineItems.map(item =>
            item.price_list_item_id || item.tenant_site_price_item_id
        );

        // Filter selected items that are not already in the current quotation
        const itemsToAdd = selectedQuotationLineItems
            .filter(item =>
                selectedLineItemIds.includes(item.price_list_item_id) &&
                !currentLineItemIds.some(id =>
                    String(id) === String(item.price_list_item_id) ||
                    String(id) === String(item.tenant_site_price_item_id)
                )
            )
            .map(item => ({
                ...item,
                multiplier: getMultiplier(item.multiplier),
                price: parseFloat(parseCurrency(item.price) || 0).toFixed(2),
                sn_hours_day: parseFloat(item.sn_hours_day || 0).toFixed(2),
                sn_hours_night: parseFloat(item.sn_hours_night || 0).toFixed(2),
                ena_hours_day: parseFloat(item.ena_hours_day || 0).toFixed(2),
                ena_hours_night: parseFloat(item.ena_hours_night || 0).toFixed(2),
                cw_hours_day: parseFloat(item.cw_hours_day || 0).toFixed(2),
                cw_hours_night: parseFloat(item.cw_hours_night || 0).toFixed(2),
                catering: parseFloat(parseCurrency(item.catering) || 0).toFixed(2),
                cleaning: parseFloat(parseCurrency(item.cleaning) || 0).toFixed(2),
                laundry: parseFloat(parseCurrency(item.laundry) || 0).toFixed(2),
                other: parseFloat(parseCurrency(item.other) || 0).toFixed(2),
            }));

        if (itemsToAdd.length === 0) {
            showToast('All selected items are already in the current quotation.', 'warning');
            return;
        }

        // Add the new items to the current line items and sort
        const updatedLineItems = sortItemsWithDefaultsFirst([...lineItems, ...itemsToAdd]);
        setLineItems(updatedLineItems);

        // Recalculate totals
        const newTotals = calculateTotals(updatedLineItems, hourlyRates);
        setTotals(newTotals);

        // Close modal and reset state
        setShowCopyModal(false);
        setSelectedQuotationId('');
        setSelectedQuotationLineItems([]);
        setSelectedLineItemIds([]);

        showToast(`Successfully copied ${itemsToAdd.length} item(s) to the quotation.`);
    };

    if (noItems) {
        return (
            <div>
                <div className="container mt-4">
                    <h1>No Items Available</h1>
                    <p>There are no items available to add to the quotation at the moment.</p>
                </div>
            </div>
        );
    }

    // Handle loading state
    if (loadingQuotation) {
        return (
            <LoadingSpinner
                size="lg"
                variant="primary"
                message="Loading quotation..."
            />
        );
    }

    // Handle error state from API
    if (Quotation && Quotation.Error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger">
                    <h4>Unable to Load Quotation</h4>
                    <p>This quotation could not be loaded. It may have been deleted, or you may not have permission to view it.</p>
                    <Button variant="primary" onClick={() => window.location.reload()}>
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    // Quotation is loaded and no error - proceed to render
    return (
        <div>
            <div id="quotation-content" className="pqd-page">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                    <h1 className="pqd-title">Quotation Details</h1>
                    {logoUrl && <img src={logoUrl} alt="Logo" style={{ height: '56px', objectFit: 'contain' }} />}
                </div>

                <div className="pqd-meta-grid">
                    <div>
                        <div className="pqd-meta-label">Customer</div>
                        <div className="pqd-meta-value">{CustomerName || '—'}</div>
                    </div>
                    <div>
                        <div className="pqd-meta-label">Quotation ID</div>
                        <div className="pqd-meta-value">{formatQuotationId(Quotation?.quotation_id) || '—'}</div>
                    </div>
                    <div>
                        <div className="pqd-meta-label">Created</div>
                        <div className="pqd-meta-value">
                            {formatCreatedDate(Quotation?.date_created || Quotation?.DateCreated || dateCreated)}
                        </div>
                    </div>
                    <div>
                        <div className="pqd-meta-label">Service Plan</div>
                        <div className="pqd-meta-value">{carePlanID != null ? (
                            embedded && typeof onOpenPlan === 'function' ? (
                                <span
                                    className="pqd-meta-link"
                                    onClick={() => {
                                        if (edit && !isSaved) {
                                            showToast('Please save your changes before continuing.', 'warning');
                                            return;
                                        }
                                        onOpenPlan();
                                    }}
                                >
                                    {formatCarePlanId(carePlanID)}
                                </span>
                            ) : (
                                <Link to={`/careplans/${id}/${carePlanID}`}>{formatCarePlanId(carePlanID)}</Link>
                            )
                        ) : <span style={{ color: 'var(--theme-text-muted)' }}>—</span>}</div>
                    </div>
                    {Quotation?.status !== undefined && (
                        <div>
                            <div className="pqd-meta-label">Status</div>
                            <div className="pqd-meta-value">
                                <span style={{
                                    display: 'inline-block',
                                    padding: '2px 10px',
                                    borderRadius: '12px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    background: Quotation.status === 1 ? '#d1e7dd' : '#e9ecef',
                                    color: Quotation.status === 1 ? '#0f5132' : 'var(--theme-text-secondary)',
                                }}>
                                    {Quotation.status === 1 ? 'Active' : 'Draft'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="pqd-card">
                    <div className="pqd-card-header">
                        <h2>Overview</h2>
                    </div>
                    <div className="pqd-summary-grid">
                    <div className={`pqd-summary-table-wrap${isRefreshingCarePlanTotals ? ' is-loading' : ''}`}>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th colSpan="4" style={{ textAlign: 'center' }}>
                                    <strong>Summary of Service Plan Hours</strong>
                                </th>
                            </tr>
                            <tr>
                                <th></th>
                                <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Hours" /></th>
                                <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Hours" /></th>
                                <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Hours" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Day</td>
                                <td>{safeFormatNumber(displayPlanHours.sn_hours_day, 2)}</td>
                                <td>{safeFormatNumber(displayPlanHours.ena_hours_day, 2)}</td>
                                <td>{safeFormatNumber(displayPlanHours.cw_hours_day, 2)}</td>
                            </tr>
                            <tr>
                                <td>Night</td>
                                <td>{safeFormatNumber(displayPlanHours.sn_hours_night, 2)}</td>
                                <td>{safeFormatNumber(displayPlanHours.ena_hours_night, 2)}</td>
                                <td>{safeFormatNumber(displayPlanHours.cw_hours_night, 2)}</td>
                            </tr>
                            <tr>
                                <td>Total</td>
                                <td>{safeFormatNumber(displayPlanHours.sn_hours_day + displayPlanHours.sn_hours_night, 2)}</td>
                                <td>{safeFormatNumber(displayPlanHours.ena_hours_day + displayPlanHours.ena_hours_night, 2)}</td>
                                <td>{safeFormatNumber(displayPlanHours.cw_hours_day + displayPlanHours.cw_hours_night, 2)}</td>
                            </tr>
                        </tbody>
                    </Table>
                    {isRefreshingCarePlanTotals && (
                        <div className="pqd-summary-table-overlay" aria-live="polite" aria-busy="true">
                            <LoadingSpinner size="sm" variant="secondary" message="Updating hours…" />
                        </div>
                    )}
                    </div>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th colSpan="4" style={{ textAlign: 'center' }}>
                                    <strong>Summary of Quotation Hours</strong>
                                </th>
                            </tr>
                            <tr>
                                <th></th>
                                <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Hours" /></th>
                                <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Hours" /></th>
                                <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Hours" /></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Day</td>
                                <td>{safeFormatNumber(totals.sn_hours_day / 60, 2)}</td>
                                <td>{safeFormatNumber(totals.ena_hours_day / 60, 2)}</td>
                                <td>{safeFormatNumber(totals.cw_hours_day / 60, 2)}</td>
                            </tr>
                            <tr>
                                <td>Night</td>
                                <td>{safeFormatNumber(totals.sn_hours_night / 60, 2)}</td>
                                <td>{safeFormatNumber(totals.ena_hours_night / 60, 2)}</td>
                                <td>{safeFormatNumber(totals.cw_hours_night / 60, 2)}</td>
                            </tr>
                            <tr>
                                <td>Total</td>
                                <td>{safeFormatNumber((totals.sn_hours_day + totals.sn_hours_night) / 60, 2)}</td>
                                <td>{safeFormatNumber((totals.ena_hours_day + totals.ena_hours_night) / 60, 2)}</td>
                                <td>{safeFormatNumber((totals.cw_hours_day + totals.cw_hours_night) / 60, 2)}</td>
                            </tr>
                            <tr>
                                <td>Total Labour (R)</td>
                                <td>{formatCurrency(((totals.sn_hours_day + totals.sn_hours_night) / 60) * hourlyRates.SN, 2)}</td>
                                <td>{formatCurrency(((totals.ena_hours_day + totals.ena_hours_night) / 60) * hourlyRates.ENA, 2)}</td>
                                <td>{formatCurrency(((totals.cw_hours_day + totals.cw_hours_night) / 60) * hourlyRates.CW, 2)}</td>
                            </tr>
                        </tbody>
                    </Table>
                    <div className={`pqd-summary-table-wrap pqd-summary-grid-span${isRefreshingCarePlanTotals ? ' is-loading' : ''}`}>
                    <LabourDifferenceSummary
                        planHours={displayPlanHours}
                        quoteHours={{
                            sn_hours_day: (totals.sn_hours_day || 0) / 60,
                            sn_hours_night: (totals.sn_hours_night || 0) / 60,
                            ena_hours_day: (totals.ena_hours_day || 0) / 60,
                            ena_hours_night: (totals.ena_hours_night || 0) / 60,
                            cw_hours_day: (totals.cw_hours_day || 0) / 60,
                            cw_hours_night: (totals.cw_hours_night || 0) / 60,
                        }}
                    />
                    {isRefreshingCarePlanTotals && (
                        <div className="pqd-summary-table-overlay" aria-live="polite" aria-busy="true">
                            <LoadingSpinner size="sm" variant="secondary" message="Updating hours…" />
                        </div>
                    )}
                    </div>
                    <div className="pqd-summary-grid-span">
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th colSpan="2" style={{ textAlign: 'center' }}>
                                    <strong>Summary of Benefits</strong>
                                </th>
                            </tr>
                            <tr>
                                <th>Benefit Type</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Body</td>
                                <td>{safeFormatNumber(benefitsTotals.benefit_body, 2)}</td>
                            </tr>
                            <tr>
                                <td>Mind</td>
                                <td>{safeFormatNumber(benefitsTotals.benefit_mind, 2)}</td>
                            </tr>
                            <tr>
                                <td>Social</td>
                                <td>{safeFormatNumber(benefitsTotals.benefit_social, 2)}</td>
                            </tr>
                        </tbody>
                    </Table>
                    </div>
                    </div>
                </div>
                <div className="pqd-card">
                <div className="pqd-section-intro">
                    <h3>Quotation</h3>
                </div>
                {edit && (
                    <div className="mb-4 p-3 border rounded" style={{ backgroundColor: 'var(--theme-surface-muted)' }}>
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <h4 className="mb-0" style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--theme-text)' }}>
                                Add Item to Quotation
                            </h4>
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                {quotationTenantSite?.site_name && (
                                    <span className="badge bg-info" style={{ fontSize: '0.8rem', fontWeight: 500 }}>
                                        {quotationTenantSite.site_name}
                                    </span>
                                )}
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={!hasPermission(PERMISSIONS.QUOTATION_UPDATE)}
                                    data-tooltip-id={`copy-quotation-tooltip`}
                                    style={{
                                        cursor: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'pointer' : 'not-allowed',
                                        opacity: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 1 : 0.5
                                    }}
                                    onClick={() => {
                                        if (hasPermission(PERMISSIONS.QUOTATION_UPDATE)) {
                                            handleOpenCopyModal();
                                        }
                                    }}
                                >
                                    Copy from Previous Quotation
                                </Button>
                                <Button
                                    disabled={selectedItems.length === 0 || !edit || !hasPermission(PERMISSIONS.QUOTATION_UPDATE) || isAddingItems}
                                    style={{
                                        cursor: (hasPermission(PERMISSIONS.QUOTATION_UPDATE) && !isAddingItems) ? 'pointer' : 'not-allowed',
                                        opacity: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 1 : 0.5,
                                        minWidth: '130px'
                                    }}
                                    onClick={() => {
                                        if (hasPermission(PERMISSIONS.QUOTATION_UPDATE) && selectedItems.length > 0 && edit && !isAddingItems) {
                                            handleAddLineItem();
                                        }
                                    }}
                                >
                                    {isAddingItems
                                        ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</>
                                        : `Add ${selectedItems.length > 1 ? `${selectedItems.length} Items` : 'Line Item'}`}
                                </Button>
                                {bulkRemoveLineItemIds.size > 0 && (() => {
                                    const selectedItemsForRemove = lineItems.filter(item => bulkRemoveLineItemIds.has(item.id));
                                    const bulkRemoveBlocked = !isAdmin && selectedItemsForRemove.some(item => isDefaultQuoteItem(item));
                                    const bulkBtn = (
                                        <Button
                                            variant="danger"
                                            disabled={bulkRemoveBlocked}
                                            onClick={bulkRemoveBlocked ? undefined : handleBulkRemoveLineItems}
                                            style={bulkRemoveBlocked ? { pointerEvents: 'none' } : undefined}
                                        >
                                            Remove {bulkRemoveLineItemIds.size} Selected
                                        </Button>
                                    );
                                    if (!bulkRemoveBlocked) return bulkBtn;
                                    return (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={<BootstrapTooltip id="admin-quote-bulk-rm-tip">{ADMIN_ONLY_BULK_REMOVE_DEFAULTS_TIP}</BootstrapTooltip>}
                                        >
                                            <span className="d-inline-block" style={{ cursor: 'not-allowed' }}>{bulkBtn}</span>
                                        </OverlayTrigger>
                                    );
                                })()}
                            </div>
                        </div>
                        <SearchableCheckboxPicker
                            label="Select Items"
                            searchPlaceholder="Search price list items..."
                            items={sortedItems}
                            getItemId={(item) => item.price_list_item_id}
                            getItemLabel={(item) => item.item_name}
                            getItemDescription={(item) => item.description || item.metadata?.description || ''}
                            isDefaultItem={(item) => defaultItems.some((def) =>
                                String(def) === String(item.price_list_item_id) ||
                                String(def) === String(item.tenant_site_price_item_id)
                            )}
                            isAlreadyAdded={(item) => isPriceItemAlreadyOnQuotation(item)}
                            selectedItems={selectedItems}
                            onSelectionChange={setSelectedItems}
                            defaultSectionTitle="Default Items"
                            allSectionTitle="All Items"
                            selectedNoun="item"
                            showDefaultBadge
                        />
                    </div>
                )}
                <div className="pqd-toolbar">
                <Form.Control
                    type="text"
                    placeholder="Search by Item Name or Description..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="mb-0 search-bar"
                />
                <div className='table-controls'>
                    <Button
                        variant="link"
                        className='btn-accent'
                        onClick={() => setCollapsed(!collapsed)}
                    >
                        {collapsed ? <FaAngleRight /> : <FaAngleLeft />}
                        {collapsed ? "Expand Columns" : "Collapse Columns"}
                    </Button>
                </div>
                </div>
                {/* Helper to check default status */}
                {(() => {
                    const checkIsDefault = (item) => isDefaultQuoteItem(item);
                    const theadBlock = (scopeFilter, isDefaultsGroup) => (
                        <thead>
                            <tr>
                                {edit && <th rowSpan="2" style={{ paddingLeft: '20px', verticalAlign: 'middle' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                        <span>Actions</span>
                                        <button type="button" className="btn btn-link p-0 text-decoration-none" style={{ fontSize: '0.75rem', lineHeight: 1 }}
                                            onClick={() => {
                                                const groupIds = filteredItems.filter(scopeFilter).map(i => i.id).filter(Boolean);
                                                const allSelected = groupIds.length > 0 && groupIds.every(id => bulkRemoveLineItemIds.has(id));
                                                const next = new Set(bulkRemoveLineItemIds);
                                                if (allSelected) { groupIds.forEach(id => next.delete(id)); } else { groupIds.forEach(id => next.add(id)); }
                                                setBulkRemoveLineItemIds(next);
                                            }}
                                        >
                                            {(() => { const ids = filteredItems.filter(scopeFilter).map(i => i.id).filter(Boolean); return ids.length > 0 && ids.every(id => bulkRemoveLineItemIds.has(id)) ? 'Deselect All' : 'Select All'; })()}
                                        </button>
                                    </div>
                                </th>}
                                <th rowSpan="2" onClick={() => handleSort('item_name')} style={{ textAlign: 'center' }}>Item {getSortIndicator('item_name')}</th>
                                <th rowSpan="2" onClick={() => handleSort('description')} style={{ textAlign: 'center' }}>Description {getSortIndicator('description')}</th>
                                <th rowSpan="2" onClick={() => handleSort('Multiplier', true)} style={{ textAlign: 'center' }}>Multiplier {getSortIndicator('Multiplier')}</th>
                                <th rowSpan="2" onClick={() => handleSort('price', true)} style={{ textAlign: 'center' }}>Price(R) {getSortIndicator('price')}</th>
                                <th rowSpan="2" onClick={() => handleSort('frequency')} style={{ textAlign: 'center' }}>Frequency {getSortIndicator('frequency')}</th>
                                <th colSpan="2" style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Minutes" /></th>
                                <th colSpan="2" style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Minutes" /></th>
                                <th colSpan="2" style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Minutes" /></th>
                                <th colSpan="4" style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }}>Additional Costs</th>
                            </tr>
                            <tr>
                                <th onClick={() => handleSort('sn_hours_day', true)} style={{ textAlign: 'center' }}>Day {getSortIndicator('sn_hours_day')}</th>
                                <th onClick={() => handleSort('sn_hours_night', true)} style={{ textAlign: 'center' }}>Night {getSortIndicator('sn_hours_night')}</th>
                                <th onClick={() => handleSort('ena_hours_day', true)} style={{ textAlign: 'center' }}>Day {getSortIndicator('ena_hours_day')}</th>
                                <th onClick={() => handleSort('ena_hours_night', true)} style={{ textAlign: 'center' }}>Night {getSortIndicator('ena_hours_night')}</th>
                                <th onClick={() => handleSort('cw_hours_day', true)} style={{ textAlign: 'center' }}>Day {getSortIndicator('cw_hours_day')}</th>
                                <th onClick={() => handleSort('cw_hours_night', true)} style={{ textAlign: 'center' }}>Night {getSortIndicator('cw_hours_night')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('catering', true)}>Catering(R) {getSortIndicator('catering')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('cleaning', true)}>Cleaning(R) {getSortIndicator('cleaning')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('laundry', true)}>Laundry(R) {getSortIndicator('laundry')}</th>
                                <th style={{ display: collapsed ? 'none' : 'table-cell', textAlign: 'center' }} onClick={() => handleSort('other', true)}>Other(R) {getSortIndicator('other')}</th>
                            </tr>
                        </thead>
                    );
                    const renderRow = (item, index, isDefaultsGroup) => {
                        const itemLineId = item.id;
                        const lineTotal = parseCurrency(item.price) * getMultiplier(item.multiplier);
                        const labor = hasLaborMinutes(item);
                        const multiplierIsZero = edit
                            ? isZeroPrice(item.multiplier)
                            : isZeroPrice(getMultiplier(item.multiplier));
                        const lineIsZero = isZeroPrice(lineTotal);
                        const rowHasZeroWarning = multiplierIsZero || lineIsZero;

                        const rowStyle = isDefaultsGroup ? {
                            backgroundColor: bulkRemoveLineItemIds.has(itemLineId) ? '#ffe5e5' : '#f8fff8',
                            borderLeft: '4px solid #28a745',
                            position: 'relative'
                        } : bulkRemoveLineItemIds.has(itemLineId) ? { backgroundColor: '#ffe5e5' } : {};

                        return (
                            <tr
                                key={index}
                                className={rowHasZeroWarning ? 'zero-price-row-flag' : undefined}
                                style={rowStyle}
                            >
                                {edit && (
                                    <td style={{ whiteSpace: 'nowrap', paddingLeft: '20px', paddingRight: '12px', verticalAlign: 'middle' }}>
                                        <Form.Check type="checkbox" className="d-inline-block me-2" checked={bulkRemoveLineItemIds.has(itemLineId)}
                                            onChange={(e) => {
                                                const newSet = new Set(bulkRemoveLineItemIds);
                                                e.target.checked ? newSet.add(itemLineId) : newSet.delete(itemLineId);
                                                setBulkRemoveLineItemIds(newSet);
                                            }}
                                        />
                                        {(() => {
                                            const removeLocked = !isAdmin && checkIsDefault(item);
                                            const trashBtn = (
                                                <Button
                                                    variant="link"
                                                    className="d-inline-block p-0"
                                                    disabled={removeLocked}
                                                    onClick={removeLocked ? undefined : () => {
                                                        showConfirm(
                                                            "Are you sure you want to remove this item?",
                                                            () => handleRemoveLineItem(index),
                                                            { title: 'Remove Item', confirmLabel: 'Remove' }
                                                        );
                                                    }}
                                                    style={removeLocked ? { pointerEvents: 'none', opacity: 0.45 } : undefined}
                                                >
                                                    <FaTrash style={{ color: '#dc3545', fontSize: '1.2rem' }} />
                                                </Button>
                                            );
                                            if (!removeLocked) return trashBtn;
                                            return (
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<BootstrapTooltip id={`admin-quote-rm-tip-${itemLineId || index}`}>{ADMIN_ONLY_DEFAULT_REMOVE_TIP}</BootstrapTooltip>}
                                                >
                                                    <span className="d-inline-block" style={{ cursor: 'not-allowed' }}>{trashBtn}</span>
                                                </OverlayTrigger>
                                            );
                                        })()}
                                    </td>
                                )}
                                <td style={{ paddingLeft: '12px', verticalAlign: 'middle' }}>
                                    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                                        {isDefaultsGroup ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ fontSize: '0.85em', lineHeight: 1 }}>⭐</span>
                                                {item.item_name}
                                            </span>
                                        ) : item.item_name}
                                        {isCatalogueDetachedLine(item) && (
                                            <span style={{ fontSize: '0.75em', color: '#6c757d' }}>
                                                No longer on this site's price list.
                                            </span>
                                        )}
                                    </span>
                                </td>
                                <td>{item.description}</td>
                                <td>
                                    <div className="zero-price-cell">
                                        {!edit ? (
                                            <span>{`${item.multiplier !== null && item.multiplier !== undefined ? item.multiplier : '1'}`}</span>
                                        ) : (!isAdmin && isDefaultQuoteItem(item)) ? (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<BootstrapTooltip id={`admin-quote-mult-tip-${itemLineId || index}`}>{ADMIN_ONLY_MULTIPLIER_TIP}</BootstrapTooltip>}
                                            >
                                                <span className="d-inline-block" style={{ cursor: 'not-allowed' }}>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.1"
                                                        disabled
                                                        readOnly
                                                        value={item.multiplier === '' ? '' : (item.multiplier !== null && item.multiplier !== undefined ? item.multiplier : '1')}
                                                        style={{ pointerEvents: 'none', opacity: 0.7 }}
                                                    />
                                                </span>
                                            </OverlayTrigger>
                                        ) : (
                                            <Form.Control type="number" step="0.1"
                                                value={item.multiplier === '' ? '' : (item.multiplier !== null && item.multiplier !== undefined ? item.multiplier : '1')}
                                                onChange={(e) => handleMultiplierChange(index, e.target.value)}
                                                onBlur={(e) => handleMultiplierBlur(index, e.target.value)}
                                                onWheel={(e) => { e.currentTarget.blur(); e.preventDefault(); }}
                                            />
                                        )}
                                        {multiplierIsZero && (
                                            <span
                                                className="zero-price-chip zero-price-chip--strong"
                                                title={getZeroMultiplierMessage()}
                                                aria-label={getZeroMultiplierMessage()}
                                            >
                                                ×0
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="zero-price-cell">
                                        <span>{formatCurrency(lineTotal, 2)}</span>
                                        {lineIsZero && (
                                            <span
                                                className={`zero-price-chip ${labor ? 'zero-price-chip--strong' : ''}`}
                                                title={getZeroPriceMessage({ hasLabor: labor })}
                                                aria-label={getZeroPriceMessage({ hasLabor: labor })}
                                            >
                                                {labor ? 'R0 · labour' : 'R0'}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>{item.frequency}</td>
                                <td>{((item.sn_hours_day || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                <td>{((item.sn_hours_night || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                <td>{((item.ena_hours_day || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                <td>{((item.ena_hours_night || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                <td>{((item.cw_hours_day || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                <td>{((item.cw_hours_night || 0) * getMultiplier(item.multiplier)).toFixed(2)}</td>
                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{formatCurrency((parseCurrency(item.catering) || 0) * getMultiplier(item.multiplier), 2)}</td>
                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{formatCurrency((parseCurrency(item.cleaning) || 0) * getMultiplier(item.multiplier), 2)}</td>
                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{formatCurrency((parseCurrency(item.laundry) || 0) * getMultiplier(item.multiplier), 2)}</td>
                                <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{formatCurrency((parseCurrency(item.other) || 0) * getMultiplier(item.multiplier), 2)}</td>
                            </tr>
                        );
                    };
                    const defaultRows = filteredItems.map((item, i) => ({ item, i })).filter(({ item }) => checkIsDefault(item));
                    const nonDefaultRows = filteredItems.map((item, i) => ({ item, i })).filter(({ item }) => !checkIsDefault(item));
                    const zeroLineBanner = summarizeZeroLineWarnings(
                        filteredItems.map((item) => ({
                            ...item,
                            unitPrice: parseCurrency(item.price),
                            multiplier: item.multiplier,
                        }))
                    );
                    return (
                        <>
                            {zeroLineBanner && (
                                <div className="zero-price-warning-banner" role="status">
                                    <span className="zero-price-warning-banner__icon" aria-hidden="true">!</span>
                                    <div className="zero-price-warning-banner__body">
                                        <span className="zero-price-warning-banner__title">Zero-value lines</span>
                                        {zeroLineBanner}
                                    </div>
                                </div>
                            )}
                            {defaultRows.length > 0 && (
                                <div style={{ marginBottom: '24px' }}>
                                    <p style={{ marginBottom: '8px', fontSize: '0.8rem', fontWeight: '600', color: '#28a745', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Default Items</p>
                                    <div className='table-wrapper mb-0'>
                                        <Table striped bordered hover className="mb-0">
                                            {theadBlock(item => checkIsDefault(item), true)}
                                            <tbody>
                                                {defaultRows.map(({ item, i }) => renderRow(item, i, true))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            )}
                            <div className='table-wrapper mt- mb-4'>
                                <Table striped bordered hover className="mb-0">
                                    {theadBlock(item => !checkIsDefault(item), false)}
                                    <tbody>
                                        {nonDefaultRows.map(({ item, i }) => renderRow(item, i, false))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan={edit ? 4 : 3}><strong>Total(R)</strong></td>
                                            <td>{formatCurrency(total, 2)}</td>
                                            <td colSpan="7" style={{ display: collapsed ? 'none' : 'table-cell' }}></td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{formatCurrency(totals.catering, 2)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{formatCurrency(totals.cleaning, 2)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{formatCurrency(totals.laundry, 2)}</td>
                                            <td style={{ display: collapsed ? 'none' : 'table-cell' }}>{formatCurrency(totals.other, 2)}</td>
                                        </tr>
                                    </tfoot>
                                </Table>
                            </div>
                        </>
                    );
                })()}
                {/* </div> */}

                <div style={{ width: '100%', background: 'var(--theme-surface-muted)', border: '1px solid var(--theme-border)', borderRadius: '8px', padding: '20px 24px', marginBottom: '24px', display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px 24px', flex: '1 1 280px' }}>
                        <div>
                            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--theme-text-muted)', fontWeight: 600, marginBottom: '3px' }}>Customer</div>
                            <div style={{ fontWeight: 500, color: 'var(--theme-text)' }}>{CustomerName || '—'}</div>
                        </div>
                        {CustomerRoomNumber && (
                            <div>
                                <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--theme-text-muted)', fontWeight: 600, marginBottom: '3px' }}>Room</div>
                                <div style={{ fontWeight: 500, color: 'var(--theme-text)' }}>{CustomerRoomNumber}</div>
                            </div>
                        )}
                        <div>
                            <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--theme-text-muted)', fontWeight: 600, marginBottom: '3px' }}>Report Date</div>
                            <div style={{ fontWeight: 500, color: 'var(--theme-text)' }}>{formattedDate}</div>
                        </div>
                        {quotationTenantSite && (quotationTenantSite.tenant_name || quotationTenantSite.site_name) && (
                            <div>
                                <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--theme-text-muted)', fontWeight: 600, marginBottom: '3px' }}>Site</div>
                                <div style={{ fontWeight: 500, color: 'var(--theme-text)' }}>{quotationTenantSite.tenant_name}{quotationTenantSite.tenant_name && quotationTenantSite.site_name && ' / '}{quotationTenantSite.site_name}</div>
                            </div>
                        )}
                    </div>

                    <div style={{ flex: '0 0 auto', minWidth: '240px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '32px', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--theme-text-muted)', fontWeight: 600 }}>Total Monthly Fee</span>
                            <span style={{ fontWeight: 600, color: 'var(--theme-text)' }}>R {formatCurrency(total, 2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '32px', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--theme-text-muted)', fontWeight: 600 }}>Discount</span>
                            <span style={{ fontWeight: 600, color: parseFloat(CustomerDiscount) > 0 ? '#198754' : 'var(--theme-text-muted)' }}>{CustomerDiscount || 0} %</span>
                        </div>
                        <hr style={{ margin: '2px 0', borderColor: 'var(--theme-border)' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '32px', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, color: 'var(--theme-text)', fontSize: '0.9rem' }}>Final Quoted Price</span>
                            <span style={{ fontWeight: 700, color: 'var(--theme-accent)', fontSize: '1.05rem' }}>R {formatCurrency(total - ((total * (CustomerDiscount || 0)) / 100), 2)}</span>
                        </div>
                    </div>
                </div>

                <div className="pqd-summary-grid" style={{ marginBottom: 16 }}>
                    {/* Care Plan Activities Table */}
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Service Plan Activities</th>
                            </tr>
                        </thead>
                        <tbody>
                            {activities.length > 0 ? (
                                activities.map((activity, index) => (
                                    <tr key={index}>
                                        <td>{activity}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td>No Service Plan activities found</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    {/* Quoted Items Table */}
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>Quoted Services</th>
                                <th>Fee</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.length > 0 ? (
                                lineItems.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            {item.item_name}
                                            {isCatalogueDetachedLine(item) && (
                                                <div style={{ fontSize: '0.75em', color: '#6c757d' }}>
                                                    No longer on this site's price list.
                                                </div>
                                            )}
                                        </td>
                                        <td>R{formatCurrency(parseCurrency(item.price) * getMultiplier(item.multiplier), 2)}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td>No Services Quoted</td>
                                    <td></td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
                <div className="pqd-actions">
                    {edit ? (
                        <>
                            <Button variant="secondary" className="mt-0" onClick={() => {
                                showConfirm(
                                    "Are you sure you want to cancel? Any unsaved changes will be lost.",
                                    () => {
                                        setEdit(false);
                                        setBulkRemoveLineItemIds(new Set());
                                        setLineItems([...temp]);
                                        const calculatedTotals = calculateTotals(temp, hourlyRates);
                                        setTotals(calculatedTotals);
                                    },
                                    { title: 'Cancel Changes', confirmLabel: 'Discard Changes', confirmVariant: 'warning' }
                                );
                            }}>Cancel</Button>
                            <Button
                                variant="primary"
                                className="mt-0"
                                disabled={!hasPermission(PERMISSIONS.QUOTATION_UPDATE)}
                                style={{
                                    cursor: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'pointer' : 'not-allowed',
                                    opacity: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 1 : 0.5
                                }}
                                onClick={() => {
                                    if (hasPermission(PERMISSIONS.QUOTATION_UPDATE)) {
                                        handleUpdateQuotation();
                                    }
                                }}
                            >
                                Save Changes
                            </Button>
                            <Button
                                variant="outline-primary"
                                className="mt-0"
                                disabled={!hasPermission(PERMISSIONS.QUOTATION_CREATE)}
                                style={{
                                    cursor: hasPermission(PERMISSIONS.QUOTATION_CREATE) ? 'pointer' : 'not-allowed',
                                    opacity: hasPermission(PERMISSIONS.QUOTATION_CREATE) ? 1 : 0.5
                                }}
                                onClick={() => {
                                    if (hasPermission(PERMISSIONS.QUOTATION_CREATE)) {
                                        handleSubmit();
                                    }
                                }}
                            >
                                Save As New Quotation
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="primary"
                                className="mt-0"
                                disabled={!hasPermission(PERMISSIONS.QUOTATION_UPDATE)}
                                data-tooltip-id={`edit-quotation-tooltip`}
                                style={{
                                    cursor: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'pointer' : 'not-allowed',
                                    opacity: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 1 : 0.5
                                }}
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    if (hasPermission(PERMISSIONS.QUOTATION_UPDATE)) {
                                        setEdit(true);
                                        setTemp([...lineItems]);
                                    }
                                }}
                            >
                                Edit Quotation
                            </Button>
                            {Quotation && Quotation.quotation_id && (
                                <Button
                                    variant={Quotation.status === 1 ? 'outline-secondary' : 'success'}
                                    className="mt-0"
                                    disabled={Quotation.status === 1 || isActivating || !hasPermission(PERMISSIONS.QUOTATION_UPDATE)}
                                    data-tooltip-id={`activate-quotation-tooltip`}
                                    style={{
                                        cursor: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'pointer' : 'not-allowed',
                                        opacity: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 1 : 0.5
                                    }}
                                    onClick={() => {
                                        if (hasPermission(PERMISSIONS.QUOTATION_UPDATE)) {
                                            handleActivateQuotation();
                                        }
                                    }}
                                >
                                    {isActivating ? 'Activating...' : 'Activate'}
                                </Button>
                            )}
                        </>
                    )}
                </div>
                </div>

                <div className="pqd-card" id="summary-details">
                    <div className="pqd-card-header">
                        <h2>Quotation Summary</h2>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
                        {[
                            { label: 'Services Breakdown', data: pieDataTotals },
                            { label: `${getDisplayName('SN')} Hours / Day`, data: pieDataSN },
                            { label: `${getDisplayName('ENA')} Hours / Day`, data: pieDataENA },
                            { label: `${getDisplayName('CW')} Hours / Day`, data: pieDataCW },
                            { label: 'Benefits Breakdown', data: pieDataBenefits },
                        ].map(({ label, data }) => (
                            <div key={label} className="chart-item" style={{ background: 'var(--theme-surface-muted)', border: '1px solid var(--theme-border)', borderRadius: '8px', padding: '16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <p style={{ fontWeight: 600, color: 'var(--theme-text-secondary)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>{label}</p>
                                <div style={{ width: '240px', height: '240px' }}>
                                    <Pie data={data} options={{ maintainAspectRatio: false }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="pqd-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h5 style={{ margin: 0, fontWeight: 700, color: 'var(--theme-text)' }}>Additional Notes</h5>
                        <span style={{
                            fontSize: '0.78rem',
                            padding: '2px 10px',
                            borderRadius: '12px',
                            fontWeight: 600,
                            background: wordCount >= NOTES_WORD_LIMIT ? '#f8d7da' : wordCount >= NOTES_WORD_LIMIT * 0.9 ? '#fff3cd' : '#e9ecef',
                            color: wordCount >= NOTES_WORD_LIMIT ? '#842029' : wordCount >= NOTES_WORD_LIMIT * 0.9 ? '#664d03' : '#6c757d',
                        }}>
                            {wordCount} / {NOTES_WORD_LIMIT} words{wordCount >= NOTES_WORD_LIMIT && ' — limit reached'}
                        </span>
                    </div>
                    <Form.Group controlId="quotationNotes" style={{ marginBottom: '20px' }}>
                        <ReactQuill
                            value={notes}
                            onChange={handleNotesChange}
                            placeholder="Enter any additional notes here..."
                            modules={quillModules}
                            style={{ borderColor: wordCount >= NOTES_WORD_LIMIT ? '#dc3545' : undefined }}
                        />
                    </Form.Group>

                    <hr style={{ borderColor: '#dee2e6', margin: '4px 0 20px' }} />
                    <p style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--theme-text-muted)', marginBottom: '16px' }}>Signatures</p>
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group controlId="clientSignatureName">
                                <Form.Label style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)' }}>Client Name</Form.Label>
                                <Form.Control type="text" placeholder="" className="mb-2" />
                            </Form.Group>
                            <Form.Group controlId="clientSignatureDate">
                                <Form.Label style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)' }}>Date</Form.Label>
                                <Form.Control type="date" className="mb-0" defaultValue={new Date().toISOString().split('T')[0]} />
                            </Form.Group>
                        </div>
                        <div className="col-md-6">
                            <Form.Group controlId="companySignatureName">
                                <Form.Label style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)' }}>Company Representative</Form.Label>
                                <Form.Control type="text" placeholder="" className="mb-2" />
                            </Form.Group>
                            <Form.Group controlId="companySignatureDate">
                                <Form.Label style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)' }}>Date</Form.Label>
                                <Form.Control type="date" className="mb-0" defaultValue={new Date().toISOString().split('T')[0]} />
                            </Form.Group>
                        </div>
                    </div>
                <div className="pqd-actions">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }} >
                        {edit ? (
                            <>
                                <Button variant="secondary" className="mt-4" onClick={() => {
                                    showConfirm(
                                        "Are you sure you want to cancel? Any unsaved changes will be lost.",
                                        () => {
                                            setEdit(false);
                                            setBulkRemoveLineItemIds(new Set());
                                            setLineItems([...temp]);
                                            const calculatedTotals = calculateTotals(temp, hourlyRates);
                                            setTotals(calculatedTotals);
                                        },
                                        { title: 'Cancel Changes', confirmLabel: 'Discard Changes', confirmVariant: 'warning' }
                                    );
                                }}>Cancel</Button>
                                <Button
                                    variant="primary"
                                    className="mt-4"
                                    disabled={!hasPermission(PERMISSIONS.QUOTATION_UPDATE)}
                                    data-tooltip-id={`save-changes-tooltip`}
                                    style={{
                                        cursor: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'pointer' : 'not-allowed',
                                        opacity: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 1 : 0.5
                                    }}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        if (hasPermission(PERMISSIONS.QUOTATION_UPDATE)) {
                                            handleUpdateQuotation();
                                        }
                                    }}
                                >
                                    Save Changes
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    className="mt-4"
                                    disabled={!hasPermission(PERMISSIONS.QUOTATION_CREATE)}
                                    data-tooltip-id={`save-new-quotation-tooltip`}
                                    style={{
                                        cursor: hasPermission(PERMISSIONS.QUOTATION_CREATE) ? 'pointer' : 'not-allowed',
                                        opacity: hasPermission(PERMISSIONS.QUOTATION_CREATE) ? 1 : 0.5
                                    }}
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        if (hasPermission(PERMISSIONS.QUOTATION_CREATE)) {
                                            handleSubmit();
                                        }
                                    }}
                                >
                                    Save As New Quotation
                                </Button>
                            </>
                        ) : (
                            <>
                                <LoadingButton
                                    className="mt-4"
                                    onClick={handleCreateQuotepdf}
                                    isLoading={isDownloading}
                                    loadingText="Processing..."
                                >
                                    Download as PDF
                                </LoadingButton>
                                {Quotation && Quotation.quotation_id && (
                                    <Button
                                        variant={Quotation.status === 1 ? 'outline-secondary' : 'success'}
                                        className="mt-4"
                                        disabled={Quotation.status === 1 || isActivating || !hasPermission(PERMISSIONS.QUOTATION_UPDATE)}
                                        data-tooltip-id={`activate-quotation-tooltip-2`}
                                        style={{
                                            cursor: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'pointer' : 'not-allowed',
                                            opacity: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 1 : 0.5
                                        }}
                                        onClick={() => {
                                            if (hasPermission(PERMISSIONS.QUOTATION_UPDATE)) {
                                                handleActivateQuotation();
                                            }
                                        }}
                                    >
                                        {isActivating ? 'Activating...' : 'Activate'}
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                </div>
                </div>

                {/* Copy from Previous Quotation Modal */}
                <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Copy Line Items from Previous Quotation</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-4">
                            <Form.Label><strong>Select Quotation</strong></Form.Label>
                            {loadingQuotations ? (
                                <LoadingSpinner
                                    size="sm"
                                    variant="secondary"
                                    message="Loading quotations..."
                                />
                            ) : (
                                <Form.Control
                                    as="select"
                                    value={selectedQuotationId}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setSelectedQuotationId(value);
                                        if (value) {
                                            const [quotationId, dateCreated] = value.split('|');
                                            handleQuotationSelect(quotationId, dateCreated);
                                        } else {
                                            setSelectedQuotationLineItems([]);
                                            setSelectedLineItemIds([]);
                                        }
                                    }}
                                >
                                    <option value="">-- Select a quotation --</option>
                                    {availableQuotations.map((quote) => (
                                        <option
                                            key={quote.quotation_id}
                                            value={`${quote.quotation_id}|${quote.date_created}`}
                                        >
                                            Quotation ID: {formatQuotationId(quote.quotation_id)} - {quote.date_created}
                                            {quote.status === 1 ? ' (Active)' : ''}
                                        </option>
                                    ))}
                                </Form.Control>
                            )}
                            {availableQuotations.length === 0 && !loadingQuotations && (
                                <Form.Text className="text-muted">
                                    No other quotations available for this client.
                                </Form.Text>
                            )}
                        </Form.Group>

                        {selectedQuotationId && (
                            <>
                                <Form.Label><strong>Select Line Items to Copy</strong></Form.Label>
                                {loadingLineItems ? (
                                    <LoadingSpinner
                                        size="sm"
                                        variant="secondary"
                                        message="Loading line items..."
                                    />
                                ) : selectedQuotationLineItems.length === 0 ? (
                                    <div className="text-muted py-3 text-center">
                                        No line items found in the selected quotation.
                                    </div>
                                ) : (
                                    <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', padding: '10px' }}>
                                        <Table striped bordered hover size="sm" className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '50px', textAlign: 'center' }}>Select</th>
                                                    <th>Item Name</th>
                                                    <th>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedQuotationLineItems.map((item) => {
                                                    // Check if item is already in the quotation using both ID fields
                                                    // Also compare by item_name as fallback since backend may assign new IDs
                                                    const isAlreadyAdded = lineItems.some(lineItem => {
                                                        const idMatch = String(lineItem.price_list_item_id) === String(item.price_list_item_id) ||
                                                            String(lineItem.tenant_site_price_item_id) === String(item.price_list_item_id) ||
                                                            String(lineItem.price_list_item_id) === String(item.tenant_site_price_item_id);
                                                        // Fallback: compare by item_name since backend may assign new IDs after save
                                                        const nameMatch = lineItem.item_name && item.item_name &&
                                                            String(lineItem.item_name).trim().toLowerCase() === String(item.item_name).trim().toLowerCase();
                                                        return idMatch || nameMatch;
                                                    });
                                                    const isSelected = selectedLineItemIds.includes(item.price_list_item_id);

                                                    return (
                                                        <tr
                                                            key={item.price_list_item_id}
                                                            style={isAlreadyAdded ? {
                                                                backgroundColor: '#f8f9fa',
                                                                opacity: 0.6
                                                            } : {}}
                                                        >
                                                            <td style={{ textAlign: 'center' }}>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={isSelected}
                                                                    disabled={isAlreadyAdded}
                                                                    onChange={() => handleLineItemToggle(item.price_list_item_id)}
                                                                />
                                                            </td>
                                                            <td>
                                                                {item.item_name}
                                                                {isAlreadyAdded && (
                                                                    <span className="text-muted ms-2" style={{ fontSize: '0.85em' }}>
                                                                        (Already in quotation)
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>{item.description || '-'}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            disabled={selectedLineItemIds.length === 0 || loadingLineItems || !hasPermission(PERMISSIONS.QUOTATION_UPDATE)}
                            data-tooltip-id={`copy-selected-items-tooltip`}
                            style={{
                                cursor: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'pointer' : 'not-allowed',
                                opacity: hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 1 : 0.5
                            }}
                            onClick={() => {
                                if (hasPermission(PERMISSIONS.QUOTATION_UPDATE)) {
                                    handleCopySelectedItems();
                                }
                            }}
                        >
                            Copy Selected Items ({selectedLineItemIds.length})
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Tooltips for permission-disabled buttons - placed at root level.
                    Mount only while their anchors exist so they cannot stick open after
                    edit/view mode swaps unmount the button without a mouseleave. */}
                {!edit && (
                    <>
                        <ReactTooltip id="edit-quotation-tooltip" place="top" closeEvents={{ click: true, mouseleave: true, blur: true }}>
                            {hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'Edit Quotation' : 'You do not have permission to edit quotations'}
                        </ReactTooltip>
                        <ReactTooltip id="activate-quotation-tooltip" place="top" closeEvents={{ click: true, mouseleave: true, blur: true }}>
                            {hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'Activate Quotation' : 'You do not have permission to activate quotations'}
                        </ReactTooltip>
                        <ReactTooltip id="activate-quotation-tooltip-2" place="top" closeEvents={{ click: true, mouseleave: true, blur: true }}>
                            {hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'Activate Quotation' : 'You do not have permission to activate quotations'}
                        </ReactTooltip>
                    </>
                )}
                {edit && (
                    <>
                        <ReactTooltip id="copy-quotation-tooltip" place="top" closeEvents={{ click: true, mouseleave: true, blur: true }}>
                            {hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'Copy from Previous Quotation' : 'You do not have permission to copy quotations'}
                        </ReactTooltip>
                        <ReactTooltip id="add-line-item-tooltip" place="top" closeEvents={{ click: true, mouseleave: true, blur: true }}>
                            {hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'Add Line Item' : 'You do not have permission to add line items'}
                        </ReactTooltip>
                        <ReactTooltip id="save-changes-tooltip" place="top" closeEvents={{ click: true, mouseleave: true, blur: true }}>
                            {hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'Save Changes' : 'You do not have permission to save changes'}
                        </ReactTooltip>
                        <ReactTooltip id="save-new-quotation-tooltip" place="top" closeEvents={{ click: true, mouseleave: true, blur: true }}>
                            {hasPermission(PERMISSIONS.QUOTATION_CREATE) ? 'Save As New Quotation' : 'You do not have permission to create quotations'}
                        </ReactTooltip>
                    </>
                )}
                <ReactTooltip id="copy-selected-items-tooltip" place="top" closeEvents={{ click: true, mouseleave: true, blur: true }}>
                    {hasPermission(PERMISSIONS.QUOTATION_UPDATE) ? 'Copy Selected Items' : 'You do not have permission to copy items'}
                </ReactTooltip>
            </div>

            <Modal show={confirmModal.show} onHide={() => setConfirmModal(m => ({ ...m, show: false }))} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{confirmModal.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{confirmModal.message}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setConfirmModal(m => ({ ...m, show: false }))}>
                        Cancel
                    </Button>
                    <Button
                        variant={confirmModal.confirmVariant}
                        onClick={() => {
                            if (confirmModal.onConfirm) confirmModal.onConfirm();
                            setConfirmModal(m => ({ ...m, show: false }));
                        }}
                    >
                        {confirmModal.confirmLabel}
                    </Button>
                </Modal.Footer>
            </Modal>

            {toast.show && <div className={`app-toast app-toast-${toast.variant}`}>{toast.message}</div>}
        </div>
    );
}

export default QuotationDetail;
